/**
 * ASHPORT pure formulas.
 *
 * Imported by BOTH the Worker and the SPA. The client may DISPLAY a prediction; only the
 * server may DECIDE. Having one implementation is what stops the bust chance on a crime tile
 * from disagreeing with the roll threshold the server actually used.
 *
 * Rules for this file:
 *   - Every function is pure. No clock, no randomness, no I/O. `now` is always a parameter.
 *   - Money is integer CENTS. The design tables in GAME-DESIGN.md are written in dollars, so
 *     every payout helper multiplies by 100 and rounds. `dollars()` marks the conversion.
 *   - Rates that cross the wire are basis points, so 5500 means 55.00 percent.
 *
 * Normative source: docs/GAME-DESIGN.md. Section references are inline.
 * Where a worked table in that document disagrees with its own stated formula, the FORMULA
 * wins and the disagreement is recorded in docs/DESIGN-DELTAS.md.
 */

import {
  ENERGY_PERIOD_MS,
  HEAT_PERIOD_MS,
  NERVE_PERIOD_MS,
  RARITIES,
  type Anchor,
  type BeatRelation,
  type Cents,
  type Dollars,
  type HeatDecayReason,
  type ItemCategory,
  type JobDifficulty,
  type LaunderTier,
  type Line,
  type MasteryTier,
  type PvpIntent,
  type Rank,
  type RankRequirement,
  type Rarity,
  type Risk,
  type FamilyRole,
  type WarrantKind,
  rankIndex,
} from './types.ts'

/*
 * Re-exported because callers that reason about a RANK GAP always need both the ladder position
 * and the curves built on it, and importing one from `types` and the other from here is the shape
 * that makes somebody eventually write a second `rankIndex`.
 */
export { rankIndex }

/* ------------------------------------------------------------------ utilities */

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/* ------------------------------------------------------------------------ money units */

/**
 * The four functions that put a brand on a number, and the only ones that should.
 *
 * `Cents` and `Dollars` are branded so the compiler can tell them apart, but arithmetic on a
 * branded number widens back to a plain `number`: `a + b` where both are `Cents` is a `number`,
 * not a `Cents`. That is a feature. It means every computed money value has to pass back through
 * one of these, and each one is a place a reviewer can see the unit being asserted.
 *
 * The alternative was a bare `number` on both sides, which is what let a hundredfold display
 * error ship and typecheck clean.
 */

/** Dollars from the design tables to the integer cents the wire and the ledger use. */
export const dollars = (d: number): Cents => Math.round(d * 100) as Cents

/** An integer that is ALREADY cents. Use when the value came from storage or the wire. */
export const cents = (n: number): Cents => Math.round(n) as Cents

/** Cents back to dollars, for a formatter or a formula written in dollars. */
export const toDollars = (c: Cents): Dollars => (c / 100) as Dollars

/** Sum money without losing the brand. Rounds, because a fraction of a cent does not exist. */
export const addCents = (...parts: Cents[]): Cents =>
  Math.round(parts.reduce((n, p) => n + p, 0)) as Cents

/** Scale money by a plain multiplier: a mastery bonus, a Beat multiplier, a laundering rate. */
export const scaleCents = (c: Cents, by: number): Cents => Math.round(c * by) as Cents

/** Basis points from a 0..1 rate. 0.55 becomes 5500. */
export const toBp = (rate: number): number => Math.round(rate * 10_000)

/* ------------------------------------------------- section 2.2 lazy regeneration */

export interface Settled {
  value: number
  at: number
}

/**
 * The lazy-regeneration rule. NEVER tick; compute from a timestamp on read.
 *
 * Three things an implementer must not get wrong, all encoded here:
 *  1. `at` advances by whole ticks, never to `now`. Advancing to `now` silently discards up to
 *     one period of accrued progress on every read.
 *  2. When the bar is full, park `at` at `now`. Otherwise `elapsed` grows unbounded while
 *     capped and the first spend after a long absence instantly refills the bar.
 *  3. A cap that DROPS clamps the stored value down on the next read, which is why the
 *     full-bar branch returns `cap` rather than `value`.
 */
export function settle(value: number, at: number, now: number, periodMs: number, cap: number): Settled {
  if (value >= cap) return { value: cap, at: now }
  const elapsed = now - at
  if (elapsed <= 0) return { value, at }
  const ticks = Math.floor(elapsed / periodMs)
  if (ticks <= 0) return { value, at }
  const next = Math.min(cap, value + ticks)
  return { value: next, at: next >= cap ? now : at + ticks * periodMs }
}

/**
 * Heat is the same machinery in the opposite direction: a floor of 0 instead of a cap, and a
 * VARIABLE period. A `periodMs` of 0 means decay is frozen (Hitlist, Lay Low), in which case
 * the anchor is untouched and the player comes back to exactly the Heat they left with.
 */
export function decay(value: number, at: number, now: number, periodMs: number, floor = 0): Settled {
  if (periodMs <= 0) return { value, at: now }
  if (value <= floor) return { value: floor, at: now }
  const elapsed = now - at
  if (elapsed <= 0) return { value, at }
  const ticks = Math.floor(elapsed / periodMs)
  if (ticks <= 0) return { value, at }
  const next = Math.max(floor, value - ticks)
  return { value: next, at: next <= floor ? now : at + ticks * periodMs }
}

/** The client mirror. Display only, and it agrees with `settle` by construction. */
export const deriveAnchor = (a: Anchor, nowMs: number): number =>
  Math.min(a.max, a.value + Math.floor(Math.max(0, nowMs - a.asOfMs) / a.periodMs))

/** Seconds until the next visible increment. Display only. */
export const nextTick = (at: number, now: number, periodMs: number): number =>
  Math.ceil((periodMs - (Math.max(0, now - at) % periodMs)) / 1000)

/* ------------------------------------------------------------ section 2.1 caps */

/** E_max(L) = 90 + floor(1.5 * L), hard cap 240. At the level cap of 60 this is 180. */
export const energyMax = (level: number): number => Math.min(240, 90 + Math.floor(1.5 * level))

/** N_max = 8 + Nerve Milestones earned, hard cap 40. 38 milestones exist, so 2 are slack. */
export const nerveMax = (milestones: number): number => Math.min(40, 8 + Math.max(0, milestones))

/* ------------------------------------------------- Phase 24: the Line at Soldier */

/**
 * THE LINE, DESIGN-DELTAS 1ae.1. One choice at Soldier, once an Era, no respec.
 *
 * The Shift is a tenth more Energy bar, on top of the level curve: nine points at level 1,
 * eighteen at the cap. The Edge is two points of Nerve, which is a quarter of the starting bar
 * and a twentieth of the full one. Both are one sentence, both are published on the profile and
 * the Street row, neither is bought and neither compounds with anything else: the multiplier is
 * fixed and the points are flat. 7.2's "no allocation screen" is met by a screen with one
 * question that asks it once.
 */
export const LINE_ENERGY_BP = 1_000
export const LINE_NERVE_POINTS = 2
export const LINE_RANK: Rank = 'Soldier'

/** The Energy cap for a level, with the Line applied. `energyMax` alone is the curve. */
export const lineEnergyMax = (level: number, line: Line): number =>
  line === 'energy' ? Math.floor((energyMax(level) * (10_000 + LINE_ENERGY_BP)) / 10_000) : energyMax(level)

/** The Nerve cap for a milestone count, with the Line applied. */
export const lineNerveMax = (milestones: number, line: Line): number =>
  nerveMax(milestones) + (line === 'nerve' ? LINE_NERVE_POINTS : 0)

export const LEVEL_CAP = 60

/**
 * Heat decay period in milliseconds per point. The design modifies the PERIOD, not the amount,
 * so the lazy-regen machinery stays identical. GAME-DESIGN 3.6.
 */
export const HEAT_DECAY_MULT: Record<HeatDecayReason, number> = {
  street: 1.0,
  go_legit: 1.35,
  lie_low: 2.5,
  jail: 2.0,
  hospital: 1.6,
  hitlist: 0,
  lay_low: 0,
}

/**
 * What running legit costs, and it is the same number for a Job and for a Crime.
 *
 * ONE CONSTANT BECAUSE IT WAS TWO BARE LITERALS. `runJobStep` and `commitCrime` each wrote `0.55`
 * inline, which is how the asymmetry that Phase 15 fixed got in: the job path had it and the crime
 * path did not, so the strictly correct play was to run legit permanently and commit crimes
 * through it. Two independent statements of a number cannot disagree if there is only one.
 *
 * It is also PUBLISHED, on `GET /api/heat`, because a 45 percent cut on every payout in the game
 * is the largest standing trade a player can be inside and the Heat panel never said so. Same
 * discipline as the Captain's price and the laundering rate: the number that executes is the
 * number on the screen.
 */
export const GO_LEGIT_PAYOUT_MULT = 0.55

/**
 * PHASE 20. The most runs one tap of a job step may be. Three, because Bronze is three and the
 * 90-second session wants three taps to Bronze, not nine. A batch is ONE tokened request and ONE
 * write run on the player object: the anti-automation ladder sees one gesture for one decision,
 * every run inside it rolls its own word off the Era keystream, and the Energy for all of them
 * is checked before any is spent. DESIGN-DELTAS 1aa.
 */
export const JOB_RUN_MAX = 3

/** What a payout KEEPS while running legit, in basis points. 5500 means 55 percent kept, 45 lost. */
export const goLegitPayoutBp = (): number => Math.round(GO_LEGIT_PAYOUT_MULT * 10_000)

/**
 * How long after stopping before a player may run legit again. SIX HOURS.
 *
 * THE PRICE IS ON THE SWITCH, because it cannot be anywhere else. The cut is charged per payout,
 * off `heat_reason` at the instant a Job or a Crime pays; the decay bonus accrues per millisecond
 * of wall clock. With both directions free and instant, the play is to stop before acting, take
 * every payout whole, and start again before idling: the bonus in full, the cut never once. Any
 * per-payout price is free to avoid by not acting, so a per-payout mechanic cannot fix this.
 *
 * Six hours makes running legit a decision about a STRETCH OF THE DAY rather than a switch to flip
 * around each action, which is what the trade was always meant to be. It is long enough that a
 * player with two or three sessions a day chooses once, and short enough that a decision made in
 * the morning is not still binding at night. Stopping stays free and instant, because the trap this
 * exit was built to remove was that there was no way out at all.
 */
export const GO_LEGIT_RESTART_MS = 6 * 3_600_000

/**
 * `rateMult` scales the decay RATE, so a smaller number is a longer period and slower cooling.
 * The Sweep passes 0.7 for "Heat decay 30 percent slower"; everything else passes nothing.
 * A reason whose multiplier is already zero (Hitlist, Lay Low) stays frozen at any rate.
 */
export const heatDecayPeriodMs = (reason: HeatDecayReason, rateMult = 1): number => {
  const m = HEAT_DECAY_MULT[reason] * rateMult
  return m <= 0 ? 0 : Math.round(HEAT_PERIOD_MS / m)
}

/* ------------------------------------------------------- section 6.2 XP curve */

/** xp_required(L -> L+1) = round(20 * L^1.75). */
export const xpRequired = (level: number): number =>
  level >= LEVEL_CAP ? Number.POSITIVE_INFINITY : Math.round(20 * Math.pow(level, 1.75))

/** xp = (2 + 0.6 * L) * E_cost, times 1.25 for Crimes. */
export const xpEarned = (level: number, energyCost: number, isCrime: boolean): number =>
  Math.round((2 + 0.6 * level) * energyCost * (isCrime ? 1.25 : 1))

/** Cumulative XP needed to stand at `level` from level 1. */
export function xpCumulative(level: number): number {
  let total = 0
  for (let l = 1; l < level; l++) total += xpRequired(l)
  return total
}

/* ------------------------------------------------------------- section 4 the Beat */

export interface BeatMultipliers {
  payout: number
  heat: number
  bust: number
  raidNerve: number
}

export const BEAT: Record<BeatRelation, BeatMultipliers> = {
  hot: { payout: 1.4, heat: 2.0, bust: 1.35, raidNerve: 1.25 },
  cooling: { payout: 1.15, heat: 1.3, bust: 1.1, raidNerve: 1.0 },
  cold: { payout: 1.0, heat: 1.0, bust: 0.85, raidNerve: 1.0 },
}

/**
 * The single most important number in section 4. The +40 percent Hot payout applies only to
 * the FIRST 40 Energy a player spends in each hot rotation. After that the payout multiplier
 * drops to 1.00 while the Heat multiplier STAYS at 2.00, so overrunning the cap is strictly
 * worse than not playing. This is what stops the published schedule from being the game's
 * number one botting vector, and it needs no detection and no enforcement.
 */
export const BEAT_BONUS_ENERGY_CAP = 40

/**
 * Payout multiplier for spending `energyCost` when `energyAlreadySpentInRotation` has already
 * gone into the hot district this rotation. Blends the bonus and flat rates across the
 * boundary so a single 10-Energy crime that straddles it is not all-or-nothing.
 */
export function beatPayoutMult(
  relation: BeatRelation,
  energyCost: number,
  energyAlreadySpentInRotation: number,
): number {
  if (relation !== 'hot') return BEAT[relation].payout
  if (energyCost <= 0) return BEAT.hot.payout
  const remainingBonus = clamp(BEAT_BONUS_ENERGY_CAP - energyAlreadySpentInRotation, 0, energyCost)
  const flat = energyCost - remainingBonus
  return (remainingBonus * BEAT.hot.payout + flat * 1.0) / energyCost
}

/* -------------------------------------------------- section 3.3 the bust curve */

/** P_base(H) = 0.02 + 0.90 / (1 + exp(-(H - 72) / 9)). Published in-game as a graph. */
export const bustBase = (heat: number): number => 0.02 + 0.9 / (1 + Math.exp(-(heat - 72) / 9))

/** R_crime by risk tier. */
export const RISK_BUST: Record<Risk, number> = { low: 0.55, medium: 1.0, high: 1.45, extreme: 1.9 }

/** R_risk payout multiplier by risk tier. */
export const RISK_PAYOUT: Record<Risk, number> = { low: 1.0, medium: 1.35, high: 1.85, extreme: 2.5 }

/** Base Heat by risk tier, before Beat multiplier and variance. */
export const RISK_HEAT: Record<Risk, number> = { low: 3, medium: 6, high: 11, extreme: 17 }

/** Nerve cost by risk tier. */
export const RISK_NERVE: Record<Risk, number> = { low: 0, medium: 0, high: 1, extreme: 2 }

/**
 * The anti-bot term. DISCLOSED but NOT QUANTIFIED to the player: the Heat panel says only that
 * working too fast for too long raises risk beyond what the number shows.
 * A human burst session never reaches A60 = 25. A script sits on it permanently.
 */
export const densityMult = (actionsLast60Min: number): number =>
  1 + 0.3 * clamp((actionsLast60Min - 25) / 50, 0, 1)

export interface BustInput {
  heat: number
  risk: Risk
  beat: BeatRelation
  /** Energy-spending actions in the last 60 minutes. */
  actionsLast60Min: number
}

/** P_bust = clamp(P_base(H) * R_crime * B_bust * D_density, 0.01, 0.95). */
export function bustProbability(i: BustInput): number {
  return clamp(
    bustBase(i.heat) * RISK_BUST[i.risk] * BEAT[i.beat].bust * densityMult(i.actionsLast60Min),
    0.01,
    0.95,
  )
}

export const bustBp = (i: BustInput): number => toBp(bustProbability(i))

/* ------------------------------------------------------- section 3.4 bust costs */

export interface BustCost {
  confiscated: Cents
  jailMinutes: number
  heatAfter: number
}

/**
 * `jailMult` is the Arrest Warrant's 1.6x, and it is applied INSIDE the 3.4 clamp rather than
 * outside it. A level 60 player under an Arrest Warrant would otherwise serve 77 minutes against
 * a ceiling this document publishes as 90, and the ceiling is the number a player can read.
 */
export function bustCost(
  level: number,
  dirtyOnHand: Cents,
  heat: number,
  jailMult = 1,
): BustCost {
  return {
    confiscated: cents(Math.min(Math.floor(dirtyOnHand * 0.25), dollars(40_000 * (1 + 0.11 * level)))),
    jailMinutes: Math.round(clamp(level * 0.8 * jailMult, 5, 90)),
    heatAfter: Math.max(0, heat - 20),
  }
}

/* ------------------------------------------------------------------------- the Ward */

/**
 * THE JAIL HAS AN EXIT. `docs/PHASE-6B.md` section 5.
 *
 * Everything below had zero or one caller before Stage 3: `bustOutChance` was implemented and
 * called by nothing at all, `bailPrice` had exactly one caller and it was a mock display line on
 * the Crimes screen, and `server/src/game/planner.ts` shipped a live string telling the player
 * "Bail is on the Home screen" when there was no bail control on any screen and no bail route in
 * the server. GAME-DESIGN:1780 deliberately engineers a first bust during onboarding, so the
 * game's only failure state had no exit and it was on the path of every new account.
 */

/**
 * `bail = max($500, L * minutes_remaining * 60)`, in CLEAN cash. Burned, never paid to the inmate.
 *
 * **The coefficient moved from 210 to 60 and the derivation is the whole point.** At 210, bailing
 * a peer out of a full sentence costs 88 to 98 minutes of the payer's own clean income, so nobody
 * would ever press the button and the mechanic would exist without being used. At 60 it is 28 to
 * 29 minutes across the whole level range: expensive enough to be a real gift, cheap enough that
 * a stranger does it.
 *
 * The ratio holds flat across levels because bail scales on `L * minutes` while income scales on
 * `(1 + 0.11L) * energy cap`, which is why only the coefficient needed changing and not the shape.
 *
 * The floor exists because a level 1 account with two minutes left would otherwise be worth $120,
 * and a favour that costs nothing is not a favour.
 */
/**
 * `warrantMult` is the Bench Warrant's 1.5x, applied AFTER the floor.
 *
 * After, because the floor exists so that a favour never costs nothing, and multiplying the floor
 * would make the warrant's effect invisible on exactly the short sentences where the floor binds.
 * A level 1 account with two minutes left is $500 either way; the warrant shows up the moment the
 * real price does.
 */
export const BAIL_COEFFICIENT = 60
export const BAIL_FLOOR = dollars(500)
export const bailPrice = (level: number, minutesRemaining: number, warrantMult = 1): Cents =>
  cents(
    Math.round(
      Math.max(BAIL_FLOOR, dollars(level * Math.max(0, minutesRemaining) * BAIL_COEFFICIENT)) *
        warrantMult,
    ),
  )

/** Bail opens 90 seconds into a sentence, so a bust is always felt before it can be undone. */
export const BAIL_OPENS_AFTER_MS = 90_000

/** Springing somebody costs Nerve and nothing else. No gate, no cash, no Energy. */
export const BUST_NERVE_COST = 4

/** An account's first bust attempt of an Era is easier, and the button says so. */
export const BUST_FIRST_OF_ERA_BONUS = 0.2

/**
 * The published chance. Stated on the button before the four Nerve are spent.
 *
 * **This replaces `bustOutChance` entirely, because the old formula ignored the inmate.** It read
 * `0.55 - minutes/400 + busterLevel/300`, so springing a level 60 Boss out of an hour-long
 * sentence was exactly as easy as springing a level 3 Associate, and the only thing that moved
 * the number was who was doing the pushing.
 *
 * The minutes term is steeper here, `/150` against `/400`, on purpose: a fresh sentence should be
 * hard and the last five minutes easy. That is what makes "bust the hardest one first" a real
 * strategy rather than a flat toll on the list.
 */
export function bustChance(i: {
  busterLevel: number
  inmateLevel: number
  minutesRemaining: number
  /** Attempts by this account in the trailing six hours, before this one. */
  bustsLast6h: number
  firstOfEra: boolean
  /** A live Marker against this inmate. See `MARKER_BUST_BONUS`. */
  marker: boolean
}): number {
  const base =
    0.62 +
    (i.busterLevel - i.inmateLevel) / 220 -
    Math.max(0, i.minutesRemaining) / 150 -
    0.05 * Math.min(6, Math.max(0, i.bustsLast6h)) +
    (i.firstOfEra ? BUST_FIRST_OF_ERA_BONUS : 0) +
    (i.marker ? MARKER_BUST_BONUS : 0)
  return clamp(base, 0.05, 0.85)
}

/**
 * A failed bust puts the BUSTER in a cell, for a fraction of what the inmate had left.
 *
 * Four to twelve minutes, plus the four Nerve already spent. The buster joins the Ward list,
 * which is how the list stays populated between crime busts: a jail with only crime in it empties
 * every time the city goes quiet.
 */
export const bustFailMinutes = (minutesRemaining: number): number =>
  clamp(Math.round(Math.max(0, minutesRemaining) * 0.35), 4, 12)

/**
 * Heat moved from the inmate to the buster on a successful bust.
 *
 * `0.4 * minutesRemaining`, clamped to 2 and 12, and the coefficient is not chosen: jail Heat
 * decays at 24 an hour, which is exactly 0.4 a minute, so the transfer is PRECISELY the Heat the
 * inmate forfeits by leaving early. A rescue is Heat-neutral for the person being rescued and
 * honestly priced for the rescuer. The flat 8 this replaces is correct at exactly 20 minutes and
 * wrong everywhere else.
 */
export const bustHeatTransfer = (minutesRemaining: number): number =>
  clamp(Math.round(0.4 * Math.max(0, minutesRemaining)), 2, 12)

/**
 * EVERY exit from jail grants ten minutes of immunity, including serving the full term.
 *
 * This is the fix for Torn's worst property, where jail shields you and being busted out strips
 * the shield, so a favour is mechanically indistinguishable from an attack. Here, leaving early
 * is never a worse position than sitting it out, which is why nobody needs the Seal switch
 * defensively and why the list stays healthy. It also stops an attacker camping a release time
 * that is public on the board.
 */
export const RELEASE_SHIELD_MS = 10 * 60_000

/**
 * Self-bail: DIRTY cash, at an escalating multiple, deliberately poor value against waiting.
 *
 * The multiple climbs with every self-bail inside a rolling 24 hours, so the exit a player buys
 * alone gets worse the more they lean on it, while the exit another player provides never does.
 * That gap is the entire social pressure of the Ward.
 */
export const SELF_BAIL_MULTIPLES = [2.5, 4, 6.5, 10] as const
export const selfBailPrice = (
  level: number,
  minutesRemaining: number,
  priorToday: number,
  warrantMult = 1,
): Cents =>
  cents(
    Math.round(
      bailPrice(level, minutesRemaining, warrantMult) *
        SELF_BAIL_MULTIPLES[Math.min(SELF_BAIL_MULTIPLES.length - 1, Math.max(0, priorToday))],
    ),
  )

/**
 * THE MARKER. The piece none of the reference games have.
 *
 * Torn manufactures the FIRST contact between strangers, and nothing in the genre manufactures
 * the SECOND, which is where a relationship actually forms. When somebody outside your Family
 * bails or busts you out you receive one: 72 hours, single use, non-transferable. If that person
 * is jailed while it is live, your bail price for them halves and your bust chance against their
 * cell gets +0.10.
 *
 * It sells no power, and that is structural rather than a policy: the only thing it discounts is
 * a favour you do for somebody else.
 */
export const MARKER_TTL_MS = 72 * 3_600_000
export const MARKER_BAIL_DISCOUNT = 0.5
export const MARKER_BUST_BONUS = 0.1

/**
 * Nobody's Era ends in a cell.
 *
 * At close minus sixty minutes every live term clamps to five minutes; at close minus ten the
 * Ward stops accepting new sentences at all.
 */
export const WARD_CLAMP_BEFORE_CLOSE_MS = 60 * 60_000
export const WARD_CLAMPED_MINUTES = 5
export const WARD_CLOSED_BEFORE_CLOSE_MS = 10 * 60_000

/* ---------------------------------------------------- section 2.5 the laundering ladder */

/** Cumulative rate bonus in percentage points by infrastructure tier. */
export const LAUNDER_TIER_BONUS: Record<LaunderTier, number> = { 0: 0, 1: 0.08, 2: 0.18, 3: 0.3 }

/** Passive Heat FLOOR imposed by owning the tier. The richest players are the most visible. */
export const LAUNDER_TIER_HEAT_FLOOR: Record<LaunderTier, number> = { 0: 0, 1: 5, 2: 12, 3: 25 }

export const LAUNDER_TIER_COST: Record<LaunderTier, Cents> = {
  0: cents(0),
  1: dollars(900_000),
  2: dollars(4_200_000),
  3: dollars(18_000_000),
}

/** The four rungs, named. GAME-DESIGN 2.5's own table, so the ladder reads as places. */
export const LAUNDER_TIER_NAME: Record<LaunderTier, string> = {
  0: 'The Street Fence',
  1: 'Laundromat',
  2: 'Check-Cashing Storefront',
  3: 'The Cage',
}

/**
 * launder_rate(H, tier) = min(0.85, 0.55 + bonus(tier)) - 0.0012 * max(0, H - 50)
 *
 * `extraPenalty` carries the additive modifiers that are not a function of Heat: the Seizure
 * Warrant (-0.04), RICO Watch (-0.06), the Chambers Key artefact (+0.03), Family Laundering
 * branch (up to +0.05) and the Precinct Ledger evidence set (+0.03).
 */
export function launderRate(heat: number, tier: LaunderTier, extraPenalty = 0): number {
  const base = Math.min(0.85, 0.55 + LAUNDER_TIER_BONUS[tier])
  return clamp(base - 0.0012 * Math.max(0, heat - 50) + extraPenalty, 0, 0.95)
}

export const launderRateBp = (heat: number, tier: LaunderTier, extraPenalty = 0): number =>
  toBp(launderRate(heat, tier, extraPenalty))

export interface LaunderResult {
  dirtyIn: Cents
  cleanOut: Cents
  haircut: Cents
  rateBp: number
}

/** Integer cents in, integer cents out, and the haircut is exactly the difference. */
export function launder(dirtyIn: Cents, heat: number, tier: LaunderTier, extraPenalty = 0): LaunderResult {
  const rate = launderRate(heat, tier, extraPenalty)
  const cleanOut = cents(Math.floor(dirtyIn * rate))
  return { dirtyIn, cleanOut, haircut: cents(dirtyIn - cleanOut), rateBp: toBp(rate) }
}

/* ------------------------------------------------------- section 3.5 the fence fee */

/**
 * fence_fee(H) = 0.08 + 0.22 * (H / 100)^1.6
 *
 * `warrantPoints` adds the Search Warrant's +4 points at Indictment Progress 30.
 */
export const fenceFee = (heat: number, warrantPoints = 0): number =>
  clamp(0.08 + 0.22 * Math.pow(clamp(heat, 0, 100) / 100, 1.6) + warrantPoints / 100, 0, 0.95)

export const fenceFeeBp = (heat: number, warrantPoints = 0): number => toBp(fenceFee(heat, warrantPoints))

/* --------------------------------------------------- section 3.6 the Precinct Captain */

/**
 * cost_per_point = (2400 + 900 * min(clears_this_era, 20)) * (1 + 0.11 * L)
 *
 * `clearOrdinal` is 1-based: the first clearing of the Era is 1. The escalation never resets
 * inside an Era, which is what stops the Captain becoming a flat toll. Paid in DIRTY cash.
 */
export function precinctCostPerPoint(clearOrdinal: number, level: number, discount = 0): Cents {
  const base = (2400 + 900 * clamp(clearOrdinal, 1, 20)) * (1 + 0.11 * level)
  return dollars(base * (1 - clamp(discount, 0, 0.9)))
}

/** RICO Watch multiplies the Captain's price by 2.2. The Aiello Envelope takes 20 percent off. */
export const RICO_PRECINCT_MULT = 2.2
export const AIELLO_ENVELOPE_DISCOUNT = 0.2

/* --------------------------------------------------------- section 3.8 RICO */

export const RICO_THRESHOLD = 90

/**
 * IP += 4 + round(0.4 * (H - threshold)) on every Energy-spending or PvP action while H >= threshold.
 *
 * The threshold is a PARAMETER with the ordinary value as its default, which is the shape
 * `fenceFee(heat, warrantPoints = 0)` and `launderRate(heat, tier, extraPenalty = 0)` already
 * have. It exists so The Sweep can drop it to 85 for the last two weeks without a second copy of
 * the curve, and it takes a NUMBER rather than a flag so there is exactly one place that decides
 * which number: `sweepFacts`.
 */
export const indictmentGain = (heat: number, threshold = RICO_THRESHOLD): number =>
  heat < threshold ? 0 : 4 + Math.round(0.4 * (heat - threshold))

/** IP decays 6 per hour while H < 75. */
export const INDICTMENT_DECAY_PER_HOUR = 6
export const INDICTMENT_DECAY_BELOW_HEAT = 75

/**
 * The six rungs, ascending, and the `satisfies` is not decoration.
 *
 * `WarrantKind` and this array were written separately and agreed by coincidence of typing: the
 * array was `as const` and nothing tied it to the union, so a seventh rung, a renamed kind or a
 * typo would have compiled and then failed at a runtime lookup. `FAMILY_ROLES` at `types.ts:100`
 * explains at length why this project keeps its enums as data so "the enum, the validator and the
 * SQL CHECK cannot drift apart"; the ladder was the odd one out until Phase 9 read it.
 */
export const WARRANT_LADDER = [
  { ip: 15, kind: 'bench' },
  { ip: 30, kind: 'search' },
  { ip: 45, kind: 'arrest' },
  { ip: 60, kind: 'wiretap' },
  { ip: 80, kind: 'seizure' },
  { ip: 100, kind: 'sealed_indictment' },
] as const satisfies readonly { ip: number; kind: WarrantKind }[]

/**
 * The artwork per rung, spelled out because the ids do NOT suffix uniformly.
 *
 * Five are `icons.warrant.<kind>_warrant` and the sixth is `icons.warrant.sealed_indictment` with
 * no suffix at all. A template would be shorter and would produce one dangling id that the content
 * gate catches only if the value reaches an `A()` call, which on a warrant panel it might not.
 */
export const WARRANT_ART: Record<WarrantKind, string> = {
  bench: 'icons.warrant.bench_warrant',
  search: 'icons.warrant.search_warrant',
  arrest: 'icons.warrant.arrest_warrant',
  wiretap: 'icons.warrant.wiretap_warrant',
  seizure: 'icons.warrant.seizure_warrant',
  sealed_indictment: 'icons.warrant.sealed_indictment',
}

/**
 * The rung a player stands on, DERIVED from Indictment Progress and never stored.
 *
 * It is a pure function of a number that is already on the row and already settles lazily, so
 * deriving it costs one comparison and cannot drift from the meter it describes. See
 * DESIGN-DELTAS 1p.3 for why that is safe: five of the six rungs price something and a price is
 * quoted at a read, and the sixth executes inside the write run that crossed it.
 *
 * The walk is DESCENDING and returns the highest rung reached, because the ladder accumulates.
 * A player at IP 80 is under a Seizure Warrant AND still pays 1.5x bail.
 */
export function warrantAt(indictment: number): WarrantKind | null {
  for (let i = WARRANT_LADDER.length - 1; i >= 0; i--) {
    if (indictment >= WARRANT_LADDER[i].ip) return WARRANT_LADDER[i].kind
  }
  return null
}

/**
 * Parse a rung back out of a stored or mirrored string, and refuse anything that is not one.
 *
 * `player_index.warrant` is `TEXT NOT NULL DEFAULT ''` because a read model with nullable columns
 * makes every consumer handle a case the writer cannot produce. This is the one place that turns
 * the empty string, and anything a future migration leaves behind, back into `null` rather than
 * into a rung nobody is standing on.
 */
export const rungOf = (raw: string): WarrantKind | null =>
  WARRANT_LADDER.find((r) => r.kind === raw)?.kind ?? null

/** Rung position, so "at or above" is a comparison rather than a list membership test. */
const rungIndex = (w: WarrantKind | null): number =>
  w === null ? -1 : WARRANT_LADDER.findIndex((r) => r.kind === w)

const atOrAbove = (w: WarrantKind | null, rung: WarrantKind): boolean =>
  rungIndex(w) >= rungIndex(rung)

/** Bench Warrant, IP 15: somebody buying you out pays half again. */
export const WARRANT_BAIL_MULT = 1.5
export const warrantBailMult = (w: WarrantKind | null): number =>
  atOrAbove(w, 'bench') ? WARRANT_BAIL_MULT : 1

/** Search Warrant, IP 30: four points onto the fence fee, at every Heat. */
export const WARRANT_FENCE_POINTS = 4
export const warrantFencePoints = (w: WarrantKind | null): number =>
  atOrAbove(w, 'search') ? WARRANT_FENCE_POINTS : 0

/** Arrest Warrant, IP 45: a CRIME bust holds you 1.6x as long. Not the rescuer's sentence. */
export const WARRANT_JAIL_MULT = 1.6
export const warrantJailMult = (w: WarrantKind | null): number =>
  atOrAbove(w, 'arrest') ? WARRANT_JAIL_MULT : 1

/** Wiretap Authorisation, IP 60. Its effect is a PUBLICATION rather than a price. */
export const WARRANT_PUBLISHES_CONTRACTS: ReadonlySet<WarrantKind> = new Set<WarrantKind>([
  'wiretap',
  'seizure',
  'sealed_indictment',
])

/** Sealed Indictment, IP 100. The ONE rung that executes rather than quoting. */
export const WARRANT_EXECUTES: ReadonlySet<WarrantKind> = new Set<WarrantKind>([
  'sealed_indictment',
])

/** Seizure Warrant, IP 80: four points off the laundering rate. RICO Watch: six more. */
export const WARRANT_LAUNDER_PENALTY = -0.04
export const RICO_LAUNDER_PENALTY = -0.06

/**
 * The whole additive laundering modifier, which is a warrant term PLUS a Heat term.
 *
 * They stack rather than replacing each other: RICO Watch is a state you are in at Heat 90, and
 * the Seizure Warrant is a rung you reached at IP 80, and it is entirely possible to hold one
 * without the other. A player who works up to IP 80 and then cools to Heat 74 keeps the warrant
 * and loses the RICO term, which is exactly the shape 3.6's decay is for.
 */
export const launderExtraPenalty = (
  w: WarrantKind | null,
  heat: number,
  threshold = RICO_THRESHOLD,
): number =>
  (atOrAbove(w, 'seizure') ? WARRANT_LAUNDER_PENALTY : 0) +
  (heat >= threshold ? RICO_LAUNDER_PENALTY : 0)

/* ------------------------------------------------------------- section 10.2 The Sweep */

/**
 * THE SWEEP. Weeks 11 and 12, and it is a CONSTANT.
 *
 * GAME-DESIGN 10.2 lists six changes for the last two weeks. DESIGN-DELTAS 1j.14 ruled the
 * finale fixed and identical in every Era and forbade any Era variant from touching Heat, RICO,
 * the Sweep Legend multiplier or Control regeneration, because Era 6's authored headline was
 * already a duplicate of the finale and a variant that collides with the finale makes the last
 * two weeks of that Era mean nothing.
 *
 * **The exclusion is enforced by SHAPE and not by a warning.** `sweepFacts` takes one boolean and
 * returns a frozen object built from module constants. There is no parameter through which an Era
 * could supply a number, `content/eras.json` has no field that could carry one (the content gate
 * refuses unknown keys), and `server/test/era.test.ts` asserts both. A rule nobody can reach is
 * better than a rule nobody may break.
 *
 * Two of the six are wired in Phase 10 and four are not. `docs/DESIGN-DELTAS.md` 1q.6 names the
 * four and why, rather than leaving them to be discovered.
 */
export const SWEEP_RICO_THRESHOLD = 85

/**
 * "Heat decay 30 percent slower." A RATE multiplier, not a period one.
 *
 * `heatDecayPeriodMs` divides `HEAT_PERIOD_MS` by the reason's multiplier, so a rate 30 percent
 * lower is a multiplier of 0.7 and a period 1/0.7 longer. Writing it as 1.3 would have made Heat
 * decay 30 percent FASTER during the finale, which is the wrong direction on the one screen the
 * finale is about.
 */
export const SWEEP_HEAT_RATE_MULT = 0.7

/** "All Legend awards 2x." Nothing awards Legend yet; 1q.5 says so rather than implying it. */
export const SWEEP_LEGEND_MULT = 2

export interface SweepFacts {
  active: boolean
  ricoThreshold: number
  heatRateMult: number
  legendMult: number
}

const SWEEP_OFF: SweepFacts = Object.freeze({
  active: false,
  ricoThreshold: RICO_THRESHOLD,
  heatRateMult: 1,
  legendMult: 1,
})

const SWEEP_ON: SweepFacts = Object.freeze({
  active: true,
  ricoThreshold: SWEEP_RICO_THRESHOLD,
  heatRateMult: SWEEP_HEAT_RATE_MULT,
  legendMult: SWEEP_LEGEND_MULT,
})

/**
 * The only door to the finale's numbers.
 *
 * One boolean in, one frozen record out. Every consumer reads this rather than the constants, so
 * "which numbers does the finale use" has exactly one answer and adding a seventh effect is a
 * field here rather than a search.
 */
export const sweepFacts = (active: boolean): SweepFacts => (active ? SWEEP_ON : SWEEP_OFF)

/* -------------------------------------------------------- section 3.7 the Scanner */

/**
 * The Heat at which a player enters the Scanner's published set at all.
 *
 * It is the WATCHED floor rather than the DANGEROUS one, and that is a correction to
 * `server/migrations/era/0001_init.sql:62`, which says `heat >= 50` is "the only band the Scanner
 * publishes". GAME-DESIGN 3.2 disagrees: the Watched row publishes a district COUNT, with no
 * handle and no row, and a count of Watched-or-above is 3.7's first row type. Both are honoured
 * by making the table's membership threshold 25 and its partial INDEX's threshold 50.
 */
export const SCANNER_PUBLISH_HEAT = 25

/** And the Heat at which a player becomes a NAMED row rather than a number in a count. */
export const SCANNER_NAMED_HEAT = 50

/**
 * When this Heat anchor falls out of the published set, as a wall-clock instant.
 *
 * **The one column that makes a lazily-settled resource queryable, and the reason `scanner_heat`
 * stores an anchor rather than a value.** Heat decays at read time from `(heat, heat_at, period)`
 * and there is no sweep anywhere, so a mirror of a SETTLED value is wrong the instant it is
 * written: a player who logs off at 92 is still at 92 a week later and the wanted list becomes a
 * list of people who went home.
 *
 * Three properties follow from it and each closes a hole this repository has already paid for:
 *
 *   A STALE ROW AGES OUT ON ITS OWN. `WHERE cools_at > now` is `server/src/lib/ward.ts`'s stated
 *   rule for `jail_until` applied to the one resource that decays continuously.
 *
 *   `cools_at DESC` IS settled-Heat DESC, exactly. Settled Heat is
 *   `heat - floor((now - heat_at) / period)`, so `heat_at + heat * period` equals
 *   `settled * period + now`, and for any fixed period the two orderings are the same
 *   permutation. A wanted list is an indexed range scan that never computes a decay to sort.
 *
 *   THE TIE GROUP IS EMPTY BY CONSTRUCTION. Ordering a bracketed list by the leaderboard's order
 *   returns an arbitrary slice of a tie group, found twice already in `payload.browse` and the
 *   Fight List. Four thousand accounts at Heat 0 cannot collide here: a row below 25 is not in
 *   the table, and this is a millisecond timestamp.
 *
 * A period of 0 does not decay at all, which is the Hitlist and Lay Low, so it never cools.
 */
export const coolsAt = (heat: number, heatAt: number, periodMs: number): number =>
  periodMs <= 0
    ? Number.MAX_SAFE_INTEGER
    : heatAt + Math.max(0, heat - (SCANNER_PUBLISH_HEAT - 1)) * periodMs

/* ------------------------------------------------- section 3.8 the Sealed Indictment */

/**
 * What the Sealed Indictment takes, and 3.8's table is exact about all five.
 *
 * NOT taken, and the list matters as much as the list above it: all clean cash, equipped weapon
 * and armor and vehicle, territory, Family membership, Family rank, persistent Legend, titles,
 * cosmetics, badges, front businesses and laundering infrastructure.
 */
export const SEIZURE_DIRTY_RATE = 0.4
export const SEIZURE_GOODS_RATE = 0.25
export const SEIZURE_JAIL_MS = 4 * 60 * 60_000
export const SEIZURE_HEAT_AFTER = 45
export const SEIZURE_LEGEND = 250

/** One stack the seizure may draw from. Equipped items are filtered out by the CALLER. */
export interface Seizable {
  slotId: string
  /** What ONE of them is worth. The item's base value, which is what every other valuation uses. */
  unitValue: Cents
  qty: number
}

export interface SeizureLine {
  slotId: string
  qty: number
  value: Cents
}

export interface Seizure {
  /** The whole unequipped holding, valued. Published, because 25 percent of what is the question. */
  holdings: Cents
  /** `floor(holdings * 0.25)`. What the seizure is aiming at. */
  target: Cents
  /** What it actually takes, which is at or just over the target unless nothing is left. */
  taken: Cents
  lines: SeizureLine[]
}

/**
 * "25 percent by value of unequipped contraband and gear" when it does not divide.
 *
 * An inventory is stacked rows with quantities, so a quarter of it by value is not a number of
 * items and 3.8 does not say what to do about that. DESIGN-DELTAS 1p.4 rules it:
 *
 * **Descending unit value, whole units, until the cumulative value reaches the target. The last
 * unit may overshoot.** Three alternatives were considered and each has a hole this one does not.
 * `floor(qty * 0.25)` per stack makes every stack of three immune and every unique item immune,
 * which is a total dodge for exactly the players holding the most valuable things. `ceil(qty *
 * 0.25)` takes a hundred percent from somebody holding one of everything. And stopping BEFORE the
 * target rather than after it means a player holding a single object worth four times the target
 * loses nothing at all, which is asset structuring as a free action.
 *
 * Taking the good stuff first is also the only version that reads as a seizure, and it leaves a
 * counterplay the player can act on: **equipped gear is never eligible**, so the answer to a
 * Seizure Warrant is to put your best weapon on, and the answer to the ladder is to be liquid in
 * clean cash. The itemised list is published BEFORE it fires, from this same function, so the
 * preview and the event cannot disagree.
 *
 * The sort is by unit value descending and then by `slotId`, because two items of equal value must
 * not be ordered by whatever the SQL cursor happened to return: a preview and an execution one
 * request apart would take different objects and the player would be told a lie.
 */
export function seizeGoods(items: readonly Seizable[], rate = SEIZURE_GOODS_RATE): Seizure {
  const holdings = cents(
    items.reduce((n, i) => n + Math.max(0, i.unitValue) * Math.max(0, i.qty), 0),
  )
  const target = cents(Math.floor(holdings * clamp(rate, 0, 1)))
  const lines: SeizureLine[] = []
  let taken = 0
  if (target <= 0) return { holdings, target, taken: cents(0), lines }

  const order = [...items]
    .filter((i) => i.qty > 0 && i.unitValue > 0)
    .sort((a, b) => b.unitValue - a.unitValue || (a.slotId < b.slotId ? -1 : 1))

  for (const item of order) {
    if (taken >= target) break
    // How many whole units it takes to reach the target from here, capped by the stack.
    const want = Math.min(item.qty, Math.ceil((target - taken) / item.unitValue))
    if (want <= 0) continue
    const value = cents(want * item.unitValue)
    lines.push({ slotId: item.slotId, qty: want, value })
    taken += value
  }
  return { holdings, target, taken: cents(taken), lines }
}

/** PvP-inflicted Heat can NEVER carry a target above 75. Only your own Crimes go past it. */
export const PVP_HEAT_CEILING = 75

/* --------------------------------------------------------- section 2.6 payouts */

export interface PayoutInput {
  level: number
  energyCost: number
  /** District earnings tier: 1.00 starter, 1.15 mid, 1.32 Verity Hill and Dockside. */
  districtTier: number
  /** Combined Beat payout multiplier, already blended through the 40-Energy bonus cap. */
  beatPayout: number
  /** Uniform variance sample in [0,1). The SERVER supplies this; the client passes 0.5. */
  variance01: number
}

const spread = (v01: number, lo: number, hi: number): number => lo + v01 * (hi - lo)

/** dirty = 900 * E_cost * (1 + 0.11 * L) * D_tier * B_pay * V, V in [0.88, 1.12]. Zero Heat. */
export function jobPayout(i: PayoutInput, masteryBonus = 0): Cents {
  const v = spread(i.variance01, 0.88, 1.12)
  return dollars(
    900 * i.energyCost * (1 + 0.11 * i.level) * i.districtTier * i.beatPayout * v * (1 + masteryBonus),
  )
}

/** Mastery payout bonus by tier. Bronze +4%, Silver +9%, Gold +15%. */
export const MASTERY_BONUS: Record<MasteryTier, number> = {
  none: 0,
  bronze: 0.04,
  silver: 0.09,
  gold: 0.15,
}

/** Completions required to REACH each tier, cumulative. */
export const MASTERY_THRESHOLD: Record<Exclude<MasteryTier, 'none'>, number> = {
  bronze: 3,
  silver: 9,
  gold: 19,
}

export function masteryAt(runs: number): MasteryTier {
  if (runs >= MASTERY_THRESHOLD.gold) return 'gold'
  if (runs >= MASTERY_THRESHOLD.silver) return 'silver'
  if (runs >= MASTERY_THRESHOLD.bronze) return 'bronze'
  return 'none'
}

/* --------------------------------------------- Phase 22: what a beat drops, and what it needs */

/** The five difficulty bands, two beats each, by beat index. Beat 1 and 2 are Easy, 9 and 10 Extreme. */
export const JOB_DIFFICULTIES: readonly JobDifficulty[] = ['Easy', 'Medium', 'Hard', 'Very Hard', 'Extreme']

export function jobDifficulty(step: number): JobDifficulty {
  const i = Math.floor((Math.max(1, step) - 1) / 2)
  return JOB_DIFFICULTIES[Math.min(JOB_DIFFICULTIES.length - 1, i)]
}

/**
 * The chance one run of a beat drops something, by the mastery the beat is at when it runs.
 *
 * Published on the row and rolled off the Era keystream, exactly as `CONTRABAND_DROP_CHANCE` is
 * for a crime. Gold is nearly three times None, which is what makes the nineteenth run of a beat
 * worth more than the first and is the whole reason to master a beat rather than leave it at
 * Bronze the moment the next one opens. DESIGN-DELTAS 1ac.1 and 1ac.2.
 */
export const JOB_DROP_CHANCE: Record<MasteryTier, number> = {
  none: 0.08,
  bronze: 0.12,
  silver: 0.16,
  gold: 0.22,
}

/** A beat drops at ITS OWN tier, and the band decides it: Easy beats drop tier 1, Extreme tier 5. */
export const JOB_DROP_TIER: Record<JobDifficulty, number> = {
  Easy: 1,
  Medium: 2,
  Hard: 3,
  'Very Hard': 4,
  Extreme: 5,
}

/** One drop in this many is a piece of gear at the beat's tier. The rest are materials. */
export const JOB_DROP_GEAR_SHARE = 4

/** Gear from a job is never rarer than this. Rarer is the Black Market's and the Fence's to sell. */
export const JOB_DROP_GEAR_RARITY_CAP: Rarity = 'uncommon'

export interface JobDrop {
  kind: 'material' | 'gear'
  /** Index into the pool of that kind the caller supplied. Never an item id: this stays pure. */
  index: number
}

/**
 * The drop, from ONE uint32, split the way `resolveCrime` splits its fourth word: the high 16
 * bits decide whether anything drops and the low 16 decide what. Of the low 16, the bottom two
 * bits pick gear (one value in `JOB_DROP_GEAR_SHARE`) or a material, and the fourteen above
 * index the pool. An empty pool of one kind hands its share to the other, so a beat whose tier
 * has no gear under the cap still drops at the published chance.
 */
export function jobDropFrom(roll: number, chance: number, materials: number, gear: number): JobDrop | null {
  if (chance <= 0 || (materials <= 0 && gear <= 0)) return null
  const hi = (roll >>> 16) / 65_536
  if (hi >= chance) return null
  const lo = roll & 0xffff
  const wantGear = lo % JOB_DROP_GEAR_SHARE === 0
  const index = Math.floor(lo / JOB_DROP_GEAR_SHARE)
  if (wantGear && gear > 0) return { kind: 'gear', index: index % gear }
  if (materials > 0) return { kind: 'material', index: index % materials }
  return { kind: 'gear', index: index % gear }
}

/* ----------------------------------------------------------- Phase 22: front upgrade levels */

/** A front levels from 1 to 5. Four, for everybody, forever, is the count; this is the depth. */
export const FRONT_LEVEL_CAP = 5

/** Each level adds a quarter of the front's authored net per day. Level 5 pays double. */
export const FRONT_LEVEL_INCOME_STEP = 0.25

export const frontIncomeMult = (level: number): number =>
  1 + FRONT_LEVEL_INCOME_STEP * (Math.max(1, Math.floor(level)) - 1)

/**
 * Clean to reach a level, in DAYS of the front's own base net, on the n^1.55 family the upkeep
 * and Books curves share: two days at level 2, seventeen at level 5, thirty-six in all. Keyed to
 * the net rather than the deed's value so a Nightclub's levels cost Nightclub money and a
 * Laundromat's cost Laundromat money, which is 9.5's rule that every sink scales with power. Each
 * level pays for itself inside the Era: `sim:constraints` holds that. DESIGN-DELTAS 1ac.4.
 */
export const FRONT_UPGRADE_DAYS = 2
export const FRONT_UPGRADE_EXP = 1.55

export function frontUpgradeCost(netDaily: Cents, toLevel: number): Cents {
  const step = Math.max(1, Math.floor(toLevel) - 1)
  return cents(Math.floor(netDaily * FRONT_UPGRADE_DAYS * Math.pow(step, FRONT_UPGRADE_EXP)))
}

/** Materials to reach a level, of the material at the front's own tier: one at 2, four at 5. */
export const frontUpgradeMaterials = (toLevel: number): number => Math.max(1, Math.floor(toLevel) - 1)

/** Days for a level to pay for itself off the quarter it adds. Level 5 is 69 days of an 84 day Era. */
export const frontUpgradePaybackDays = (toLevel: number): number =>
  (FRONT_UPGRADE_DAYS * Math.pow(Math.max(1, toLevel - 1), FRONT_UPGRADE_EXP)) / FRONT_LEVEL_INCOME_STEP

/** E_cost(step_index) = 3 + step_index. Step 1 costs 4, step 7 costs 10. */
export const jobStepEnergy = (stepIndex: number): number => 3 + stepIndex

/** dirty = 1700 * E_cost * (1 + 0.11 * L) * R_risk * B_pay * V, V in [0.85, 1.15]. */
export function crimePayout(i: PayoutInput, risk: Risk, skillPoints = 0, homeDistrict = true): Cents {
  const v = spread(i.variance01, 0.85, 1.15)
  return dollars(
    1700 *
      i.energyCost *
      (1 + 0.11 * i.level) *
      RISK_PAYOUT[risk] *
      i.beatPayout *
      v *
      (1 + 0.0012 * clamp(skillPoints, 0, 100)) *
      (homeDistrict ? 1 : 0.9),
  )
}

/**
 * heat = H_base(risk) * B_heat * (1 + U(-0.18, +0.18))
 *
 * The plus or minus 18 percent is NOT flavour. It exists so the optimal Heat ceiling cannot be
 * ridden exactly: a bot that grinds to 58 and stops must leave a safety margin, and the size of
 * the margin it chooses is itself a behavioural fingerprint.
 */
export function crimeHeat(
  risk: Risk,
  beat: BeatRelation,
  variance01: number,
  /** The authored per-crime base. Defaults to the risk tier, which is what the 26 ship with. */
  base = RISK_HEAT[risk],
): number {
  return Math.max(1, Math.round(base * BEAT[beat].heat * spread(variance01, 0.82, 1.18)))
}

/** Crime skill adds +0.12% payout and -0.05 points of bust chance per point, 0 to 100. */
export const skillBustReduction = (skillPoints: number): number => clamp(skillPoints, 0, 100) * 0.0005

/** 35 percent of skill progress transfers within a crime category. */
export const SKILL_CATEGORY_TRANSFER = 0.35

/* ------------------------------------------------------------- section 7.2 PvP */

/**
 * W = P_a^0.6 / (P_a^0.6 + P_d^0.6)
 *
 * The exponent 0.6 is the whole point: it compresses stat advantage hard, so a 2x power lead
 * buys a 60 percent win rate rather than 100 percent, and a 10x lead still loses one in five.
 */
export function pvpWinRaw(attackerPower: number, defenderPower: number): number {
  const a = Math.pow(Math.max(1, attackerPower), 0.6)
  const d = Math.pow(Math.max(1, defenderPower), 0.6)
  return a / (a + d)
}

/**
 * P_win = clamp(W * sleep_mod, 0.05, 0.92), and `sleep_mod` is 1.00 BY DESIGN.
 *
 * The term stays in the comment because its value is the point: 7.5 rule 4 dampens what a PvP
 * action YIELDS inside the target's Sleep window and never the roll. Tribal Wars' flat night
 * defence bonus moved the meta to the window edges and made a third of the day dead; a personal
 * rolling window that changes the payout and not the odds relocates nothing.
 *
 * `fortifyMod` WAS A THIRD PARAMETER HERE, DEFAULTING TO 1, AND IT IS DELETED. DESIGN-DELTAS
 * 1o.10. It had no value, no source, no range and no caller anywhere in the repository, in either
 * this file or GAME-DESIGN 7.2's copy of the formula. A fortification is a property of a BLOCK and
 * Phase 7 built it there as `fort_tier` against `FORT_ABSORB_PER_TIER`; a player is not a block and
 * no verb in the design would give them one. A multiplier in a published formula with no way to
 * move it is worse than no multiplier, because the next reader has to prove the absence.
 */
export function pvpWin(attackerPower: number, defenderPower: number): number {
  return clamp(pvpWinRaw(attackerPower, defenderPower), 0.05, 0.92)
}

/** margin = (P_win - 0.5) * 2 on a win, clamped to [0, 1]. Drives payout and hospital time. */
export const pvpMargin = (pWin: number): number => clamp((pWin - 0.5) * 2, 0, 1)

/** FF = clamp(P_d / P_a, 0.15, 1.00) ^ 0.75. Applied to EVERYTHING a PvP action yields. */
export const fairFight = (attackerPower: number, defenderPower: number): number =>
  Math.pow(clamp(defenderPower / Math.max(1, attackerPower), 0.15, 1.0), 0.75)

/**
 * Power is derived from gear, level and rank only. There are no purchasable stats, no hidden
 * stat pile to invest into, and no allocation screen. Each gear term scales by Condition/100.
 */
export interface PowerInput {
  weaponPower: number
  weaponCondition: number
  armorPower: number
  armorCondition: number
  vehiclePower: number
  vehicleCondition: number
  level: number
  rank: Rank
  familyBonus: number
}

export function power(i: PowerInput): number {
  return (
    i.weaponPower * 1.0 * (clamp(i.weaponCondition, 0, 100) / 100) +
    i.armorPower * 0.85 * (clamp(i.armorCondition, 0, 100) / 100) +
    i.vehiclePower * 0.55 * (clamp(i.vehicleCondition, 0, 100) / 100) +
    40 * i.level +
    250 * rankIndex(i.rank) +
    i.familyBonus
  )
}

/** Mug take: clamp(0.06 + 0.14 * margin, 0.06, 0.20) of the target's dirty cash on hand. */
export const mugTakeRate = (margin: number): number => clamp(0.06 + 0.14 * margin, 0.06, 0.2)

/** The attacker receives 72 percent of what the target loses. The rest burns. PvP destroys money. */
export const MUG_ATTACKER_SHARE = 0.72
export const MUG_BURN_SHARE = 0.28

/** Hospitalize duration: clamp(15 + 225 * margin, 15, 240) minutes. */
export const hospitalMinutes = (margin: number): number => Math.round(clamp(15 + 225 * margin, 15, 240))

/**
 * Per-target caps. Maximum 3 intents from A against B per rolling 24 hours, rising to 5 with a
 * sworn Vendetta. This is OGame's bash rule: Nerve limits TOTAL aggression but not CONCENTRATED
 * aggression, and one obsessive player can still ruin one specific person's Era.
 */
export const perTargetCap = (vendettaSworn: boolean): number => (vendettaSworn ? 5 : 3)

/**
 * ROLLING, and that word is the rule rather than a description.
 *
 * A calendar day resets for everybody at the same instant, which turns the cap into a schedule:
 * six intents land back to back across a midnight boundary and the victim's protection is a
 * clock they have to learn. A trailing 24 hour window has no boundary to camp, which is why 7.5
 * writes "per rolling 24 hours" and why the query is `at >= now - PVP_CAP_WINDOW_MS` rather than
 * a day bucket.
 */
export const PVP_CAP_WINDOW_MS = 24 * 3_600_000

/** Diminishing returns inside the cap, by 1-based ordinal of the hit in the rolling 24 hours. */
export const REPEAT_PAYOUT: readonly number[] = [1.0, 0.4, 0.15, 0.15, 0.15]
export const REPEAT_ATTACKER_HEAT: readonly number[] = [1.0, 1.0, 1.3, 1.3, 1.3]

export const repeatPayoutMult = (ordinal: number): number =>
  REPEAT_PAYOUT[clamp(ordinal, 1, REPEAT_PAYOUT.length) - 1]

export const repeatHeatMult = (ordinal: number): number =>
  REPEAT_ATTACKER_HEAT[clamp(ordinal, 1, REPEAT_ATTACKER_HEAT.length) - 1]

/**
 * Payout scaling by rank gap, applied to reward rather than to combat power because power
 * penalties get gamed by point suppression and payout penalties do not.
 * A Boss mugging an Associate gets 10 percent of the money and pays 60 percent more Heat.
 */
export function rankGapPayout(attacker: Rank, defender: Rank): number {
  const gap = rankIndex(attacker) - rankIndex(defender)
  if (gap <= 0) return 1.0
  if (gap === 1) return 0.6
  if (gap === 2) return 0.35
  return 0.1
}

export function rankGapHeat(attacker: Rank, defender: Rank): number {
  const gap = rankIndex(attacker) - rankIndex(defender)
  if (gap <= 0) return 1.0
  if (gap === 1) return 1.2
  if (gap === 2) return 1.4
  return 1.6
}

/** Dampening inside the target's nominated 6-hour Sleep window. The ROLL is never dampened. */
export const SLEEP_MUG_SHARE = 0.25
export const SLEEP_HOSPITAL_SHARE = 0.5

/** ambush_cost = 4,000,000 * (1 - 0.11 * min(deficit, 8)), floor 640,000. Dirty cash. */
export const ambushCost = (deficit: number): Cents =>
  dollars(Math.max(640_000, 4_000_000 * (1 - 0.11 * clamp(deficit, 0, 8))))

/** The Ambush hospitalises the next aggressor for four hours flat, regardless of power. 7.3. */
export const AMBUSH_HOSPITAL_MINUTES = 240
export const AMBUSH_TTL_MS = 48 * 3_600_000

/**
 * The three intents, with everything that is a property OF the intent rather than of the fight.
 *
 * Nerve and Beef weight are GAME-DESIGN 7.1's table; the attacker's Heat is 3.9's, which prices
 * the same three verbs in a second place and is the only table in the document that gives the
 * "more than one rank below" surcharge a number. The two agree and both are reproduced here so
 * that neither can move without the other being looked at.
 */
export interface PvpIntentDef {
  id: PvpIntent
  nerve: number
  /** Attacker Heat at rank gap 0 or 1. GAME-DESIGN 3.9. */
  heat: number
  /** Attacker Heat when the target is MORE than one rank below. 3.9's second column. */
  heatStomp: number
  beefWeight: number
}

export const PVP_INTENTS: Readonly<Record<PvpIntent, PvpIntentDef>> = {
  mug: { id: 'mug', nerve: 3, heat: 4, heatStomp: 6, beefWeight: 1.0 },
  hospitalize: { id: 'hospitalize', nerve: 5, heat: 7, heatStomp: 11, beefWeight: 2.5 },
  message: { id: 'message', nerve: 2, heat: 2, heatStomp: 3, beefWeight: 1.5 },
}

/** Leave a Message adds 5 Heat to the TARGET, clamped by `PVP_HEAT_CEILING`. 7.1. */
export const MESSAGE_TARGET_HEAT = 5

/**
 * The attacker's Heat for one intent, with both multipliers 7.5 puts on it.
 *
 * The rank-gap surcharge is taken from 3.9's two-column table rather than from 7.5's
 * `rankGapHeat` ladder where the two overlap, because 3.9 states absolute Heat numbers and 7.5
 * states multipliers of a base it never names. They are consistent: 3.9's 4 to 6 for a Mug is
 * 1.5x, which sits between 7.5's 1.4 at two ranks and 1.6 at three, and 7.5's ladder is what
 * prices the gap ABOVE one rank. So the base comes from 3.9 and the multiplier from 7.5.
 */
export function pvpAttackerHeat(
  intent: PvpIntent,
  attacker: Rank,
  defender: Rank,
  ordinal: number,
): number {
  const def = PVP_INTENTS[intent]
  const gap = rankIndex(attacker) - rankIndex(defender)
  const base = gap > 1 ? def.heatStomp : def.heat
  return Math.max(1, Math.round(base * rankGapHeat(attacker, defender) * repeatHeatMult(ordinal)))
}

/**
 * EVERY PvP SCALAR MULTIPLIES THE TAKE, WHICH IS WHAT THE TARGET LOSES, AND THE ATTACKER THEN
 * RECEIVES 72 PERCENT OF THAT. DESIGN-DELTAS 1o.13.
 *
 * Two readings of 7.1 and 7.5 were available and they are not equivalent. Under the other one the
 * target loses the unscaled `mugTakeRate` fraction and the scalars only shrink the attacker's
 * share, so a Boss mugging an Associate destroys ninety percent of what he takes and the
 * Associate is robbed exactly as hard as if a peer had done it.
 *
 * This reading is the one 7.5 is FOR. Rule 3 says Fair Fight applies to "everything PvP yields:
 * mug take", and the take is what is taken. A rule that punishes the bully while leaving the
 * victim equally robbed reduces the reward for harassment without reducing the harassment, and
 * 15.1 names the survivability of a new player's week one as the thing this section decides. It
 * also keeps the 72/28 burn a constant rather than a figure swinging from 28 percent to 97
 * depending on who is fighting, which is what makes it modellable in 9.7's money supply.
 */
export interface PvpYieldInput {
  attackerPower: number
  defenderPower: number
  attacker: Rank
  defender: Rank
  /** 1-based ordinal of this intent against this target inside the rolling 24 hours. */
  ordinal: number
  /** True when the DEFENDER is inside their own nominated Sleep block. */
  targetAsleep: boolean
}

/** The product of all four scalars 7.5 puts on what a PvP action yields. Never above 1. */
export function pvpYieldScalar(i: PvpYieldInput): number {
  return (
    fairFight(i.attackerPower, i.defenderPower) *
    rankGapPayout(i.attacker, i.defender) *
    repeatPayoutMult(i.ordinal) *
    (i.targetAsleep ? SLEEP_MUG_SHARE : 1)
  )
}

/* --------------------------------------------- section 7.5 rule 4, the Sleep window */

export const SLEEP_BLOCK_HOURS = 6
/** A nomination takes effect 72 hours after it is filed. DESIGN-DELTAS 1o.6. */
export const SLEEP_NOTICE_MS = 72 * 3_600_000

/**
 * How many times an account may write its Sleep hour in an Era. GAME-DESIGN 7.5 rule 4.
 *
 * TWO, because the rule is "nominates a 6-hour block once per Era. Changeable once", and that is
 * two gestures rather than one. Every account already HAS a window from its first minute, derived
 * by `defaultSleepHour`, so the first explicit call is the nomination and the second is the change.
 * At one the second clause of the rule meant nothing. DESIGN-DELTAS 1w.3.
 *
 * One constant because it was two: the guard in `PlayerDO.nominateSleep` and the `changeable` flag
 * `routes/pvp.ts` publishes to the fight list are the same number, and they disagreed by nothing
 * only because they were both wrong in the same way.
 */
export const SLEEP_MAX_CHANGES = 2

/**
 * The DEFAULT Sleep block for an account that has never nominated one, derived from the player id.
 *
 * There has to be a default, because the dampening is a PROTECTION and an account that never
 * nominates would otherwise be the least protected player in the game, which inverts the rule it
 * comes from. Deriving it from the id rather than from signup time gives three properties at once:
 * it is stable across sessions and Eras, it spreads uniformly across all twenty-four hours so no
 * hour of the clock is dead, and it is computable by the client for display without a round trip.
 *
 * This is NOT a game outcome and deliberately does not draw from the Era keystream. It is a
 * default preference, it is published to the player from their first minute, and they may change
 * it. `server/src/lib/roll.ts` is for things a player could otherwise dispute.
 */
export function defaultSleepHour(playerId: string): number {
  let h = 2166136261
  for (let i = 0; i < playerId.length; i++) {
    h ^= playerId.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h % 24
}

/**
 * Is `now` inside the 6-hour block starting at `startHour` UTC? Wraps past midnight.
 *
 * UTC and not a player-local timezone, because the window has to be checkable by an attacker's
 * request without knowing where the target lives, and because a timezone is a thing an account
 * could otherwise change tactically.
 */
export function inSleepWindow(startHour: number, now: number): boolean {
  const hour = Math.floor(now / 3_600_000) % 24
  const from = ((startHour % 24) + 24) % 24
  const delta = (hour - from + 24) % 24
  return delta < SLEEP_BLOCK_HOURS
}

/* ------------------------------- section 7.5 rule 5, new-in-Era protection */

/**
 * Ten days, and they run from THE PLAYER'S OWN ARRIVAL rather than from Era open.
 *
 * GAME-DESIGN 7.5 writes "10 days elapsed in the Era" and the shipped code has always disagreed
 * with it: `server/src/lib/identity.ts` passes `NEW_PLAYER_SHIELD_MS` into `PlayerDO.init`, which
 * stamps `shield_until = now + shieldMs` at the moment the account is created. The code is right
 * and the document is corrected to it, DESIGN-DELTAS 1o.14: read literally, an account created on
 * Era day 40 would arrive with no protection at all, which is indefensible for exactly the reason
 * 15.1 gives. 1i.12 read the same phrase the literal way for the HOUSE VOUCH and was right to,
 * because a floor arriving early costs a late joiner nothing and a shield missing entirely costs
 * them their week one.
 */
export const NEW_ERA_PROTECTION_DAYS = 10
export const NEW_ERA_PROTECTION_CLEAN = 5_000_000
export const NEW_ERA_PROTECTION_HEAT = 40

export type ProtectionConditionId =
  | 'days'
  | 'rank'
  | 'family'
  | 'clean'
  | 'territory'
  | 'initiated'
  | 'heat'
  | 'war'

/**
 * Everything the checklist reads, resolved by the caller.
 *
 * A plain record with no clock and no I/O, on `RankFacts`' construction and for its reason: the
 * SAME function prices the list a new player reads and decides the refusal their attacker meets.
 * 7.5 requires that "the checklist is shown to the player as a list they can read", and one
 * function is what makes that a structural property rather than a screen somebody has to remember
 * to keep in step with a gate written somewhere else.
 */
export interface ProtectionFacts {
  /** When this account entered the Era. Not Era open. See `NEW_ERA_PROTECTION_DAYS`. */
  joinedAt: number
  rank: Rank
  /** 0 when they are at nobody's table. */
  familyJoinedAt: number
  clean: Cents
  heat: number
  /** PvP intents this account has INITIATED. One is enough. */
  intentsInitiated: number
  /** Stamped by the two D1 routes that own those acts, 0 when neither has happened. */
  territoryAt: number
  warWindowAt: number
  now: number
}

export interface ProtectionCondition {
  id: ProtectionConditionId
  /** What the player reads. No em-dashes, no en-dashes: the gate byte-scans this file. */
  label: string
  met: boolean
}

export interface Protection {
  standing: boolean
  /** The FIRST condition that fired, which is the one the refusal and the screen both name. */
  endedBy: ProtectionConditionId | null
  endsAt: number
  conditions: ProtectionCondition[]
}

/**
 * The eight off-switches, in the order 7.5 lists them, evaluated live.
 *
 * Nothing is written eagerly at the eight sites. `shield_reason = 'new_era'` on the player row
 * says that the shield is THIS one, and the attack gate ends it in place the first time any
 * condition has fired. That is why there is no ninth place to forget: the gate is the only reader
 * and it re-derives the whole list every time. DESIGN-DELTAS 1o.6.
 */
/**
 * "nine days", "one day". Bounded at ten by `NEW_ERA_PROTECTION_DAYS`, so a lookup rather than a
 * number-to-words implementation, and it falls back to the numeral if that constant ever grows.
 */
function daysToGo(n: number): string {
  const words = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']
  // CAPITALISED, because this word opens the second sentence. The first version of this returned
  // lowercase and shipped "Ten days in the city. ten days to go." to the live game, which is a
  // worse sentence than the numeral it replaced.
  const word = words[n] ?? String(n)
  return n === 1 ? `${word} day` : `${word} days`
}

export function newEraProtection(f: ProtectionFacts): Protection {
  const endsAt = f.joinedAt + NEW_ERA_PROTECTION_DAYS * DAY_MS
  const conditions: ProtectionCondition[] = [
    {
      id: 'days',
      /*
       * SPELLED, because every other line on this list is: "Make Soldier", "Four of them",
       * "Twenty points of Heat". This one read "Ten days in the city. 10 left." on a new account,
       * which mixes a spelled constant with a numeral in one breath and says the same number
       * twice. The count is bounded at ten by construction, so spelling it needs no general
       * machinery.
       */
      label:
        f.now >= endsAt
          ? 'Ten days in the city.'
          : `Ten days in the city. ${daysToGo(Math.max(1, Math.ceil((endsAt - f.now) / DAY_MS)))} to go.`,
      met: f.now >= endsAt,
    },
    { id: 'rank', label: 'Make Soldier.', met: rankIndex(f.rank) >= rankIndex('Soldier') },
    { id: 'family', label: 'Sit down at a Family table.', met: f.familyJoinedAt > 0 },
    { id: 'clean', label: 'Hold more than 5,000,000 clean.', met: f.clean > NEW_ERA_PROTECTION_CLEAN },
    { id: 'territory', label: 'Claim a block, or put anything into one.', met: f.territoryAt > 0 },
    { id: 'initiated', label: 'Raise a hand to somebody yourself.', met: f.intentsInitiated > 0 },
    { id: 'heat', label: 'Let your Heat past 40.', met: f.heat > NEW_ERA_PROTECTION_HEAT },
    { id: 'war', label: 'Stand on a block during a War Window.', met: f.warWindowAt > 0 },
  ]
  const fired = conditions.find((c) => c.met) ?? null
  return { standing: fired === null, endedBy: fired?.id ?? null, endsAt, conditions }
}

/* --------------------------------------------- section 8.5 territory economics */

/**
 * BLOCK INCOME. GAME-DESIGN 8.3's tiers, and the ruling that settled it.
 *
 * Two sections disagreed by roughly 250x. 8.3 gives per-block tiers summing to $1.27M clean a day
 * across the whole 40-block map; 9.7's money-supply model budgeted territory at 64,000 clean per
 * DAU per day of income against 92,000 of upkeep.
 *
 * 9.7 loses, and not on a judgement call. Its upkeep line at 5,000 DAU is $460,000,000 a day,
 * against a theoretical maximum of `upkeepDaily(40)` = **$2,433,789** for every block in the city
 * held by one family. It is 189x larger than the most the formula it shares a document with can
 * charge. It cannot be adopted in either direction, so 8.3 and the implemented curve are the
 * coherent pair.
 *
 * The consequence is worth stating plainly: territory is a SMALL share of the clean faucet, not
 * the 21 percent 9.7 claimed. Its value is control and denial, not income. That is a better game
 * anyway, because a territory layer worth 21 percent of the money supply is one the dominant
 * family compounds into.
 */
export const BLOCK_INCOME_TIERS = { starter: 22_000, mid: 34_000, prime: 52_000 } as const

/** 40 blocks across twelve districts. 18 starter, 15 mid, 7 prime. */
export const BLOCKS_IN_CITY = 40
export const BLOCK_INCOME_AVERAGE_DOLLARS = 31_750

/** upkeep_daily(n) = 8,000 * n^1.55 in CLEAN cash. The coefficient is a tuning dial. */
export const upkeepDaily = (blocks: number, coefficient = 8000): Cents =>
  blocks <= 0 ? cents(0) : dollars(coefficient * Math.pow(blocks, 1.55))

/**
 * skim(n, Books) = 0.06 * max(0, n - Books), applied to ALL blocks' income.
 * Ikariam's Corruption: blocks held beyond your Books level bleed income on every held block,
 * not just the excess. That retroactive hit is what makes over-expansion feel like a mistake.
 */
export const territorySkim = (blocks: number, books: number): number =>
  clamp(0.06 * Math.max(0, blocks - books), 0, 1)

export function territoryNetDaily(
  blocks: number,
  books: number,
  avgIncomePerBlock: Cents,
  coefficient = 8000,
): Cents {
  const gross = blocks * avgIncomePerBlock * (1 - territorySkim(blocks, books))
  return cents(gross - upkeepDaily(blocks, coefficient))
}

/** Income accrues hourly and STOPS at 60 hours uncollected. Punishes five days away, not two. */
export const TERRITORY_ACCRUAL_CAP_HOURS = 60

/** Control: 0 to 100, a successful raid knocks off 20 to 35, +1 per hour, capture resets to 25. */
export const CONTROL_MAX = 100
export const CONTROL_ON_CAPTURE = 25
export const CONTROL_REGEN_PER_HOUR = 1
export const CONTROL_RAID_MIN = 20
export const CONTROL_RAID_MAX = 35
/** A challenger must EXCEED the holder's committed score by 5 percent, not merely tie it. */
export const WAR_FLIP_MARGIN = 0.05
export const WAR_REFLIP_LOCKOUT_MS = 24 * 3600_000

/**
 * The score a challenger must beat, published so the client prints the number the server compares
 * against. 1j.12 states the rule for PvP ("publish everything a userscript would compute") and it
 * holds here for the same reason: a threshold nobody can see is a threshold somebody reverses.
 */
export const flipThreshold = (holderScore: number): number =>
  Math.floor(holderScore * (1 + WAR_FLIP_MARGIN)) + 1

/** Presence is hard-capped at five per side per block. The anti-headcount rule, 1j.11. */
export const PRESENCE_CAP_PER_SIDE = 5

export const controlRegen = (control: number, hours: number): number =>
  clamp(control + CONTROL_REGEN_PER_HOUR * Math.max(0, hours), 0, CONTROL_MAX)

/**
 * A block's daily income from its TIER, which is a property of the block TYPE. 1m.6.
 *
 * The three numbers live in `BLOCK_INCOME_TIERS` above and nowhere else, which is why
 * `content/blocks.json` authors a tier and not an income: two copies of a number is one copy too
 * many, and this is the copy the server rolls against.
 */
export const BLOCK_TIER_INCOME = [
  BLOCK_INCOME_TIERS.starter,
  BLOCK_INCOME_TIERS.mid,
  BLOCK_INCOME_TIERS.prime,
] as const

/**
 * GAME-DESIGN 8.3: "Independent blocks pay 70 percent of a player-held block's income, so they
 * are a starting position rather than a farm."
 *
 * It follows the GROUND, not the holder. The eight blocks that stand Independent at Era open pay
 * 70 percent for the whole Era to whoever holds them, so a family that takes all eight pays
 * `upkeepDaily(8)` in full against reduced income, which is exactly the disincentive 8.3 wants.
 * Recomputing it on capture would make the reduction vanish the moment it mattered.
 */
export const INDEPENDENT_INCOME_BP = 7000

/** Daily clean income for one block, before the Books skim. `tier` is 1, 2 or 3. */
export const blockIncomeDaily = (tier: number, independentOrigin: boolean): Cents => {
  const base = BLOCK_TIER_INCOME[clamp(Math.trunc(tier), 1, 3) - 1]
  return dollars(independentOrigin ? (base * INDEPENDENT_INCOME_BP) / 10_000 : base)
}

/**
 * Fortification, GAME-DESIGN 8.4: "Named tiers players can read at a glance: Open, Watched,
 * Shuttered, Barred, Sealed." Five names, so four ticks above Open.
 *
 * The names are here rather than in `content/` because they are a mechanical ladder a player
 * reads on a button, not authored flavour, and because the server has to name the tier in a
 * refusal. `FORT_TIER_MAX` is the index, not the count.
 */
export const FORT_TIERS = ['Open', 'Watched', 'Shuttered', 'Barred', 'Sealed'] as const
export type FortTier = (typeof FORT_TIERS)[number]
export const FORT_TIER_MAX = FORT_TIERS.length - 1
export const fortTierName = (tier: number): FortTier =>
  FORT_TIERS[clamp(Math.trunc(tier), 0, FORT_TIER_MAX)]

/** "Any Family member may spend 2 Energy or 1 Nerve to add one tick." 8.4. */
export const FORTIFY_ENERGY = 2
export const FORTIFY_NERVE = 1

/**
 * 1j.2: Cinder Park's blocks lose one fortification tier every 24 hours.
 *
 * This replaced CONTENT-BIBLE 4.3's "the remaining 3 sit in Cinder Park and are permanently
 * unclaimed", which is the sentence that produced the stale 43. The district nobody wants is
 * expressed as a cost of holding it rather than as a hole in the map.
 */
export const FORT_DECAY_DISTRICT = 'cinder_park'
export const FORT_DECAY_MS = 24 * 3600_000

/**
 * The territory gate, DESIGN-DELTAS 1m.1.
 *
 * The FIRST block needs twelve RANKED members off probation, which is 8.1's minimum viable roster
 * and opens on Era day 10, the first day a rank exists at all. Every block after it needs twelve
 * SPONSORED members off probation, which is 1g.1 unchanged.
 *
 * Both numbers are `FAMILY_ROSTER_MIN`. The gate that changes is which twelve.
 */
export const TERRITORY_FIRST_BLOCK_RANKED = 12
export const TERRITORY_EXPAND_SPONSORED = 12

/**
 * THE RAID, DESIGN-DELTAS 1n.4.
 *
 * 8.4 says "a successful raid in a War Window knocks off random 20 to 35", so raids can fail, and
 * nothing anywhere said what decided. Nothing stated a Nerve cost either, though
 * `BeatMultipliers.raidNerve` has been 1.25 in the Hot district since Phase 4 with no reader.
 *
 * Three Nerve, matching the only Nerve price 8.6 states for the war layer (Espionage). In the Hot
 * district it is `ceil(3 * 1.25)` which is 4, and that is `raidNerve`'s first reader.
 */
export const RAID_NERVE = 3

/**
 * Fortification SUBTRACTS from the Control a raid knocks off. It does not roll.
 *
 * Five is not chosen next to the other constants, it falls out of them: at Sealed the absorb is
 * `4 * 5` which is 20, which is exactly `CONTROL_RAID_MIN`, so **the worst roll bounces off a
 * sealed door and nothing else does.** That is one sentence a player can hold in their head, and
 * it is the reason the number is 5 rather than 6.
 */
export const FORT_ABSORB_PER_TIER = 5

/**
 * The 20 to 35 a raid rolls, from one word of the Era keystream.
 *
 * Here rather than inside the Durable Object because it is what makes the raid VERIFIABLE: at the
 * Era reveal a player recomputes the word from the secret and then this line, and if the line
 * lived beside the debit there would be nothing published to recompute it with. The word is a
 * uint32 and the span is the 16 integers 20 to 35 inclusive.
 */
export const raidControlRoll = (word: number): number =>
  CONTROL_RAID_MIN +
  Math.min(
    CONTROL_RAID_MAX - CONTROL_RAID_MIN,
    Math.floor(((word >>> 0) / 4_294_967_296) * (CONTROL_RAID_MAX - CONTROL_RAID_MIN + 1)),
  )

/** Control knocked off by one raid: the draw, less the absorb, never below zero. */
export const raidControlLoss = (roll20to35: number, fortTier: number): number =>
  Math.max(0, Math.trunc(roll20to35) - FORT_ABSORB_PER_TIER * clamp(Math.trunc(fortTier), 0, FORT_TIER_MAX))

/**
 * The chance a raid is absorbed entirely, in basis points, published on the button.
 *
 * 1j.12's rule is to publish everything a userscript would compute, and this is the one number a
 * raider would otherwise have to derive by hand. The draw is uniform over the 16 integers 20 to 35
 * inclusive, so this is the count of draws the absorb cancels over 16.
 */
export const raidAbsorbedBp = (fortTier: number): number => {
  const absorb = FORT_ABSORB_PER_TIER * clamp(Math.trunc(fortTier), 0, FORT_TIER_MAX)
  const span = CONTROL_RAID_MAX - CONTROL_RAID_MIN + 1
  const stopped = clamp(absorb - CONTROL_RAID_MIN + 1, 0, span)
  return toBp(stopped / span)
}

/**
 * A raid the door stops takes a hinge off instead. 8.4 requires it in words: "attackers chip it
 * back the same way during a window." So a fully absorbed raid chips one fortification tier, which
 * means Sealed can be worn down, no tap is ever wasted, and the failure is legible.
 */
export const raidChipsFort = (roll20to35: number, fortTier: number): boolean =>
  fortTier > 0 && raidControlLoss(roll20to35, fortTier) <= 0

/**
 * Fortification is ONE verb running in both directions, which is 8.4 read literally: "attackers
 * chip it back the same way during a window."
 *
 * On ground your family holds it raises a tier; on contested ground it takes one of the holder's
 * away, at the same 2 Energy or 1 Nerve. The direction is decided by who holds the block, so there
 * is one button and no second price to learn, and it is what makes tiers 1 to 3 reducible at all
 * (`raidChipsFort` can only ever fire at Sealed, by arithmetic).
 *
 * Raising is available at any time and lowering is window-only, because lowering is an attack and
 * 8.6 hard rule 1 says nothing about a window is actionable before it opens.
 */
export const fortifyDelta = (holderIsYou: boolean): 1 | -1 => (holderIsYou ? 1 : -1)

/**
 * THE WINDOW SCORE, DESIGN-DELTAS 1n.1.
 *
 * One point is one Nerve, or the thing 8.4 prices equal to it: a fortification bought with 2
 * Energy scores 1 because 8.4 itself says "2 Energy or 1 Nerve". Nerve is the unit because it is
 * the resource 8.6 hard rule 1 forbids stockpiling, so a score denominated in Nerve cannot be
 * banked before the window opens.
 *
 * `PRESENCE_CAP_PER_SIDE` above is the other half: a side may hold at most five slots on one
 * block in one window, and the sixth member is refused and told where there is room. Headcount
 * buys breadth because depth is REFUSED, which is stronger than depth merely being inefficient.
 */
export const FORTIFY_SCORE = 1

/**
 * An Operation commit costs exactly what a raid costs. Pooling is not a discount.
 *
 * 8.6 hard rule 1 forbids anything that lets a window be pre-loaded, and a cheaper pooled commit
 * is a way to buy more window per Nerve. What the member buys for the same price is that it takes
 * one tap and no context, which is the whole of 1n.6.
 */
export const OPERATION_COMMIT_NERVE = RAID_NERVE

/**
 * LAST CALL. In the final ten minutes of a window, ANY contributor may trigger the Operation, not
 * only the leader who declared it.
 *
 * This is what answers "what if the Capo goes to bed", and it replaces the auto-resolution the
 * first draft of 1n.6 ruled. A resolution draws from a player's keystream, and a cron drawing on a
 * sleeping leader's behalf would advance a cursor with no request behind it and no `crime_log` row
 * to recompute it from, which is the one property `server/src/lib/roll.ts` exists to protect.
 * Handing the trigger to the people who already paid for it costs nothing, keeps every roll
 * somebody's own action, and makes the failure a leadership decision rather than an accident.
 */
export const OPERATION_LAST_CALL_MS = 10 * 60_000

/**
 * What an Operation knocks off: ONE roll for everybody, and the absorb subtracted ONCE.
 *
 * Five people hitting the same door together get through it once instead of bouncing off it five
 * times separately. That is OGame's Alliance Combat System, and it is a real advantage that turns
 * on exactly when a family needs its crew: against Open ground an Operation and five solo raids
 * are the same trade, and against Sealed ground the Operation is worth four extra tiers of absorb.
 */
export const operationControlLoss = (
  roll20to35: number,
  fortTier: number,
  contributors: number,
): number =>
  Math.max(
    0,
    Math.trunc(roll20to35) * Math.max(0, Math.trunc(contributors)) -
      FORT_ABSORB_PER_TIER * clamp(Math.trunc(fortTier), 0, FORT_TIER_MAX),
  )

/**
 * The previous holder's one counter-attempt inside the siege window, at half Nerve cost. 8.6 rule
 * 3. Rounded UP, so the discount never makes a raid free.
 */
export const SIEGE_COUNTER_NERVE_BP = 5000
export const siegeCounterNerve = (base: number): number =>
  Math.max(1, Math.ceil((base * SIEGE_COUNTER_NERVE_BP) / 10_000))

/**
 * THE SEVEN PRIME STANDING EFFECTS, DESIGN-DELTAS 1n.7.
 *
 * 1j.11 requires a named economic standing effect on each of the seven prime blocks, never a
 * combat effect. The rule those seven are held to is the constraint rather than the list: **a
 * standing effect's value is bounded by the ground the holder holds, never by the number of people
 * on its roster.** Anything that scales with headcount is the advantage the presence cap exists to
 * remove, re-entering the game through the economy where the cap cannot reach it.
 *
 * The prose lives in CONTENT-BIBLE 2.3 beside the block it belongs to and reaches the server
 * through `content/blocks.json`. The NUMBERS live here, because they are what the server computes
 * against, and a number in two places is a number that will disagree with itself.
 */
export const PRIME_EFFECT_KEYS = [
  'books_desk',
  'filing_fee',
  'standing_order',
  'night_freight',
  'standing_account',
  'cold_room',
  'small_boat_channel',
] as const
export type PrimeEffectKey = (typeof PRIME_EFFECT_KEYS)[number]

/** Ashport Savings: the holder's Books count one level higher, for the skim only. */
export const EFFECT_BOOKS_BONUS = 1
/** County Courthouse: upkeep assessed at 7,360 a block-unit instead of 8,000, an eight percent cut. */
export const EFFECT_UPKEEP_COEFFICIENT = 7360
/** Brannock Works Gate: family upgrade levels cost ten percent less clean. */
export const EFFECT_UPGRADE_DISCOUNT_BP = 1000
/** Kepler Junction Depot: the till runs to 84 hours before it stops, not 60. */
export const EFFECT_ACCRUAL_CAP_HOURS = 84
/** Ward Check Cashing: the first daily reckoning a family fails flags it and takes nothing. */
export const EFFECT_UPKEEP_GRACE_SWEEPS = 1
/** Slip 3 Marina Office: Independent-origin ground pays 85 percent instead of 70. */
export const EFFECT_INDEPENDENT_INCOME_BP = 8500

/**
 * A block's daily income with the holder's standing effects applied. `blockIncomeDaily` stays the
 * unmodified authored curve, because it is what the map prints for ground you do not hold.
 */
export const blockIncomeFor = (
  tier: number,
  independentOrigin: boolean,
  effects: readonly PrimeEffectKey[],
): Cents => {
  const base = BLOCK_TIER_INCOME[clamp(Math.trunc(tier), 1, 3) - 1]
  if (!independentOrigin) return dollars(base)
  const bp = effects.includes('small_boat_channel')
    ? EFFECT_INDEPENDENT_INCOME_BP
    : INDEPENDENT_INCOME_BP
  return dollars((base * bp) / 10_000)
}

/** The upkeep coefficient this holder is assessed at. */
export const upkeepCoefficientFor = (effects: readonly PrimeEffectKey[]): number =>
  effects.includes('filing_fee') ? EFFECT_UPKEEP_COEFFICIENT : 8000

/** How many hours of till this holder's blocks accrue before generation stops. */
export const accrualCapHoursFor = (effects: readonly PrimeEffectKey[]): number =>
  effects.includes('night_freight') ? EFFECT_ACCRUAL_CAP_HOURS : TERRITORY_ACCRUAL_CAP_HOURS

/** The Books level this holder's skim is computed against. */
export const booksFor = (books: number, effects: readonly PrimeEffectKey[]): number =>
  Math.min(BOOKS_MAX, books + (effects.includes('books_desk') ? EFFECT_BOOKS_BONUS : 0))

/**
 * BOOKS, DESIGN-DELTAS 1n.8, and the number is the RESIDUAL of a derivation that already ran.
 *
 * GAME-DESIGN 9.7's clean-drain table carries one row for this sink, "Books levels and Family
 * upgrades, 54,000 per DAU per day", which over 8.1's twelve-member minimum roster and an 84 day
 * Era is $54,432,000. `FAMILY_UPGRADE_BASE` above already takes 50.9 percent of it, $27,710,128
 * for two six-level branches, and says in its own comment that "Books gets the other 49.1 percent
 * and 15 levels to spend it on, which Phase 7 prices."
 *
 * That residual is $26,721,872. `sum(L^1.55)` over L = 2..16 is 497.611, so the coefficient that
 * lands exactly on it is 53,700, and 53,000 is the round number under that: $26,373,396, or 98.7
 * percent of the residual. The exponent is 1.55, borrowed from `upkeepDaily` for the reason the
 * upgrade curve borrows it, so the game has one superlinear curve family and not three.
 *
 * Sanity check in the form the upgrade derivation uses: $26,373,396 across 12 members over 84 days
 * is $26,164 of clean per member per day against a modelled clean faucet of $381,003, which is 6.9
 * percent. Books and upgrades together are 14.1 percent against 9.7's 15 percent allocation.
 *
 * The 53,000 is derived and not authored, on the same footing as the 300,000 upgrade coefficient,
 * and it is registered in `docs/CONTENT-GAPS.md` rather than quietly adopted.
 */
export const BOOKS_BASE = 53_000
/**
 * The literal is 1.55 and not `FAMILY_UPGRADE_EXPONENT`, because that constant is declared further
 * down this file and a top-level reference to it from here is a temporal dead zone throw at import
 * time rather than a compile error. `shared/test/formulas.test.ts` asserts the two are equal, so
 * the coupling is checked rather than assumed.
 */
export const BOOKS_EXPONENT = 1.55

/** Clean cash for ONE Books level. `level` is the level being bought, so the first buy is 2. */
export const booksCost = (level: number): Cents =>
  level <= BOOKS_MIN || level > BOOKS_MAX
    ? cents(0)
    : dollars(BOOKS_BASE * Math.pow(Math.trunc(level), BOOKS_EXPONENT))

/* ------------------------------------------------------------ section 9.3 gear */

/**
 * ITEM BASE VALUE, for the four categories the bible prices by capability rather than by price.
 *
 * CONTENT-BIBLE 8.6 and 8.7 author an explicit value for every contraband item ("Fence value")
 * and every material ("Unit value"), so those two categories NEVER come through here: their
 * numbers are read straight out of the catalogue by `baseValueOf` and a curve would overwrite a
 * writer's decision with an interpolation. Weapons, armor and vehicles are authored with a
 * capability triple and no price at all, and businesses are authored with an income and an
 * upkeep and no purchase price, so those four need a ladder and this is it.
 *
 * The spans are cited rather than invented:
 *   - business 400,000 to 9,000,000 is GAME-DESIGN 9.5 sink #18, verbatim.
 *   - weapons topping out at 12,000,000 is GAME-DESIGN 9.3's own worked repair example,
 *     "Repairing a 12,000,000 Legendary from 40 to 100 costs 6,480,000", which is
 *     0.009 * 12,000,000 * 60 exactly.
 *   - armor and vehicles are scaled from weapons against the same six-tier ladder, and are the
 *     one genuinely underdetermined pair. Logged in docs/CONTENT-GAPS.md as authoring work.
 *
 * Tier carries 70 percent of the position and rarity 30, because tier is the authored power
 * ladder and rarity refines it. The interpolation is geometric rather than linear, so each step
 * up is a constant MULTIPLE: a linear ladder makes the bottom of the catalogue worthless and the
 * top a rounding error.
 */
export type PricedByCurve = 'weapons' | 'armor' | 'vehicles' | 'business'

export const ITEM_VALUE_SPAN: Record<PricedByCurve, readonly [number, number]> = {
  weapons: [8_000, 12_000_000],
  armor: [6_000, 8_000_000],
  vehicles: [20_000, 9_000_000],
  business: [400_000, 9_000_000],
} as const

/** True when this category's price comes from the curve rather than from the catalogue. */
export const pricedByCurve = (category: ItemCategory): category is PricedByCurve =>
  category === 'weapons' || category === 'armor' || category === 'vehicles' || category === 'business'

/** Position of an item on its category's value ladder, 0 at tier 1 common, 1 at tier 6 legendary. */
export function itemValuePosition(tier: number, rarity: Rarity): number {
  const t = clamp((tier - 1) / 5, 0, 1)
  const r = clamp(RARITIES.indexOf(rarity) / (RARITIES.length - 1), 0, 1)
  return 0.7 * t + 0.3 * r
}

export function itemBaseValue(category: PricedByCurve, tier: number, rarity: Rarity): Cents {
  const [lo, hi] = ITEM_VALUE_SPAN[category]
  return dollars(lo * Math.pow(hi / lo, itemValuePosition(tier, rarity)))
}

/** Repair costs 0.009 * item_base_value per Condition point, in clean cash. */
export const repairCost = (itemBaseValue: Cents, pointsRestored: number, coefficient = 0.009): Cents =>
  cents(itemBaseValue * coefficient * Math.max(0, pointsRestored))

/** The Crusher: any item converts to 8 percent of base value in materials, guaranteed. */
export const CRUSHER_RATE = 0.08
export const crusherValue = (itemBaseValue: Cents): Cents =>
  cents(Math.floor(itemBaseValue * CRUSHER_RATE))

/** Fence price floor: 2x the item's NPC Crusher value. Era-late dumping cannot zero the economy. */
export const fencePriceFloor = (itemBaseValue: Cents): Cents =>
  scaleCents(crusherValue(itemBaseValue), 2)

/* ------------------------------------------------------------- section 7.6 bounties */

/** Six tiers, six icons.bounty artworks. Returns 1..6 for a bounty amount in cents. */
export function bountyTier(amount: Cents): 1 | 2 | 3 | 4 | 5 | 6 {
  const d = amount / 100
  if (d >= 50_000_000) return 6
  if (d >= 15_000_000) return 5
  if (d >= 4_000_000) return 4
  if (d >= 1_000_000) return 3
  if (d >= 250_000) return 2
  return 1
}

/**
 * max_bounty = 0.4 * target_clean + 1.5 * target_dirty, evaluated against the TARGET's wealth,
 * never the lister's. A whale cannot perma-camp a new player because the ceiling on a new
 * player is small.
 */
export const maxBounty = (targetClean: Cents, targetDirty: Cents): Cents =>
  cents(Math.floor(0.4 * targetClean + 1.5 * targetDirty))

/** The six dirty-cash bands the Scanner publishes instead of an exact balance. */
export function dirtyBand(dirty: Cents): 1 | 2 | 3 | 4 | 5 | 6 {
  const d = dirty / 100
  if (d >= 10_000_000) return 6
  if (d >= 2_000_000) return 5
  if (d >= 500_000) return 4
  if (d >= 100_000) return 3
  if (d > 0) return 2
  return 1
}

/* ------------------------------------------------------------ section 6.1 rank */

/**
 * Rank is TWO things and conflating them is what made the ladder unclimbable.
 *
 * GAME-DESIGN 6.1: "The first three are earned by the player. The last two are SEATS inside a
 * Family, held by one person each, and they are lost when you leave the Family."
 *
 * So Associate, Soldier and Capo are a stored, earned rung, and Underboss and Boss are a
 * FUNCTION of the seat. Storing all five in one column is what let a family founder sit in the
 * Boss chair while their rank said Associate for the whole Era, which is the state Phase 6
 * actually shipped: nothing anywhere wrote the column after `init()`.
 *
 * Deriving the seat half rather than storing it also makes one class of bug impossible. A rank
 * that is not stored cannot go stale when somebody is kicked, and 6.1 requires exactly that
 * ("lost when you leave the Family").
 */
export type EarnedRank = Extract<Rank, 'Associate' | 'Soldier' | 'Capo'>

export const EARNED_RANKS = ['Associate', 'Soldier', 'Capo'] as const satisfies readonly EarnedRank[]

/**
 * The two rungs a vouch can be for. Associate is where everybody starts and nobody vouches for
 * it, and Underboss and Boss are chairs. So a vouch is always Soldier or Capo, which is exactly
 * the domain of migration 0006's `rung` column.
 */
export type VouchRung = Exclude<EarnedRank, 'Associate'>

export const nextEarnedRank = (r: EarnedRank): VouchRung | null =>
  r === 'Associate' ? 'Soldier' : r === 'Soldier' ? 'Capo' : null

/**
 * The rank a player actually holds: the higher of what they earned and what their chair says.
 *
 * The one rule that is not in 6.1's table and is in 6.3's prose, and it is load bearing:
 * **"Nothing above Associate exists without a vouch."** An unvouched player who charters a
 * family holds the Boss SEAT and the Associate RANK, because a chair is not a vouch and because
 * the alternative is that any newcomer mints themselves three sponsorship slots on Era day one
 * by paying a charter fee. Recorded in docs/DESIGN-DELTAS.md 1i.9.
 */
export const effectiveRank = (earned: EarnedRank, seat: FamilyRole | null): Rank => {
  if (earned === 'Associate') return 'Associate'
  if (seat === 'boss') return 'Boss'
  if (seat === 'underboss') return 'Underboss'
  return earned
}

/** GAME-DESIGN 6.1's gates, as numbers. Every one of them is stated in that table. */
export const RANK_SOLDIER_LEVEL = 10
export const RANK_SOLDIER_VOUCHES = 1
export const RANK_CAPO_LEVEL = 25
export const RANK_CAPO_CHAINS_AT_BRONZE = 3
export const RANK_CAPO_HEAT_CEILING = 60
export const RANK_CAPO_CLEAN: Cents = dollars(2_000_000)
/** "a SECOND vouch from a Capo or above", so two accepted vouches on file, not one. */
export const RANK_CAPO_VOUCHES = 2
export const RANK_UNDERBOSS_LEVEL = 40

const DAY_MS = 24 * 3600_000
/** "Heat below 60 for 14 CONSECUTIVE days". A maintained condition, not a passed one. */
export const RANK_CAPO_HEAT_CLEAR_MS = 14 * DAY_MS
export const RANK_CAPO_MEMBERSHIP_MS = 14 * DAY_MS

/** Whole days elapsed since an anchor, floored at zero. What the checklist prints. */
export const daysSince = (anchor: number, now: number): number =>
  anchor <= 0 ? 0 : Math.max(0, Math.floor((now - anchor) / DAY_MS))

/**
 * Everything the gate reads, resolved by the caller.
 *
 * Deliberately a plain record with no clock and no I/O, so the same function prices the
 * checklist the screen renders and decides the promotion the server writes. That is the same
 * construction that keeps the bust chance on a crime tile equal to the threshold the server
 * rolls against, and it is the reason this is here rather than in the Durable Object.
 */
export interface RankFacts {
  level: number
  clean: Cents
  /** Chains on which EVERY step stands at Bronze or better. GAME-DESIGN 6.1's "chain mastery". */
  chainsAtBronze: number
  /** The later of Era open and the last instant this player's Heat touched the ceiling. */
  heatClearSince: number
  /** 0 when they are at nobody's table. */
  familyJoinedAt: number
  /** Accepted vouches on file this Era, house or human. */
  vouches: number
  now: number
}

const req = (
  id: RankRequirement['id'],
  label: string,
  have: number | null,
  need: number | null,
  met: boolean,
  deferred = false,
): RankRequirement => ({ id, label, met, have, need, deferred })

/**
 * The checklist for the next earned rung, in the order GAME-DESIGN 6.1 states it.
 *
 * A DEFERRED condition is printed and does not block. There is exactly one today, the Soldier
 * gate's Contract, because Contracts do not exist: blocking on it would leave the ladder with no
 * bottom rung a second time, which is the failure this whole pass is closing. Logged in
 * docs/CONTENT-GAPS.md and it becomes a live condition in the change that ships Contracts.
 */
export function rankGate(from: EarnedRank, f: RankFacts): RankRequirement[] {
  if (from === 'Associate') {
    return [
      req('level', `Level ${RANK_SOLDIER_LEVEL}`, f.level, RANK_SOLDIER_LEVEL, f.level >= RANK_SOLDIER_LEVEL),
      req(
        'vouch',
        'A vouch from a Capo or above',
        f.vouches,
        RANK_SOLDIER_VOUCHES,
        f.vouches >= RANK_SOLDIER_VOUCHES,
      ),
      req('contract_helper', 'One Contract, standing as a helper', null, null, false, true),
    ]
  }
  if (from === 'Soldier') {
    const heatDays = daysSince(f.heatClearSince, f.now)
    const seatDays = daysSince(f.familyJoinedAt, f.now)
    return [
      req('level', `Level ${RANK_CAPO_LEVEL}`, f.level, RANK_CAPO_LEVEL, f.level >= RANK_CAPO_LEVEL),
      req(
        'mastery',
        `Bronze on ${RANK_CAPO_CHAINS_AT_BRONZE} district chains, every step`,
        f.chainsAtBronze,
        RANK_CAPO_CHAINS_AT_BRONZE,
        f.chainsAtBronze >= RANK_CAPO_CHAINS_AT_BRONZE,
      ),
      req(
        'heat_clear',
        `Heat under ${RANK_CAPO_HEAT_CEILING} for fourteen days straight`,
        heatDays,
        RANK_CAPO_HEAT_CLEAR_MS / DAY_MS,
        f.heatClearSince > 0 && f.now - f.heatClearSince >= RANK_CAPO_HEAT_CLEAR_MS,
      ),
      req(
        'clean_held',
        'Two million clean, in hand',
        f.clean,
        RANK_CAPO_CLEAN,
        f.clean >= RANK_CAPO_CLEAN,
      ),
      req(
        'membership',
        'Fourteen days at the same table',
        seatDays,
        RANK_CAPO_MEMBERSHIP_MS / DAY_MS,
        f.familyJoinedAt > 0 && f.now - f.familyJoinedAt >= RANK_CAPO_MEMBERSHIP_MS,
      ),
      req(
        'vouch',
        'A second vouch from a Capo or above',
        f.vouches,
        RANK_CAPO_VOUCHES,
        f.vouches >= RANK_CAPO_VOUCHES,
      ),
    ]
  }
  return []
}

/** A deferred condition is stated, never counted. See `rankGate`. */
export const gateClear = (rs: readonly RankRequirement[]): boolean =>
  rs.length > 0 && rs.every((r) => r.deferred || r.met)

/* ------------------------------------------------------------ section 6.3 sponsorship */

/** stake = 250,000 * (1 + era_week * 0.15) clean cash, held by the system, returned at 115%. */
export const sponsorStake = (eraWeek: number): Cents =>
  dollars(250_000 * (1 + Math.max(0, eraWeek) * 0.15))

export const SPONSOR_STAKE_RETURN = 1.15

/**
 * "The stake returns at 115 percent when the sponsee reaches **Soldier plus two levels**."
 * GAME-DESIGN 6.3, and 6.3 again in the slot rule: slots are "released when the sponsee reaches
 * Soldier plus two levels, or when the sponsorship is dissolved".
 *
 * **The phrase is ambiguous and the reading is a ruling.** It can be read as an ABSOLUTE
 * milestone, the Soldier gate's level plus two, or as a RELATIVE one, two levels gained after the
 * promotion. The absolute reading is taken, for three reasons and each of them is a property
 * rather than a preference:
 *
 *   - It is stateless. The relative reading needs a "level when the vouch landed" column that
 *     nothing writes today, and a stored baseline is one more thing that can go stale.
 *   - It is what "reaches" means. A player reaches level 12; they do not reach a delta.
 *   - Every other gate in the ladder is an absolute threshold read off `RankFacts`, so this one
 *     is priced by the same shape as `rankGate` and can be shown on a screen the same way.
 *
 * Recorded in docs/DESIGN-DELTAS.md. Reversible in one line if the owner reads it the other way.
 */
export const RANK_SOLDIER_SETTLED_LEVEL = RANK_SOLDIER_LEVEL + 2

/**
 * Has this sponsee reached the rung the stake was staked on?
 *
 * Rank AND level, never level alone: a level 30 Associate nobody ever vouched for has not made
 * Soldier, and the stake is on the promotion rather than on the bar.
 */
export const sponsorshipSettled = (earned: EarnedRank, level: number): boolean =>
  earned !== 'Associate' && level >= RANK_SOLDIER_SETTLED_LEVEL

/**
 * The escrow row id a sponsorship stake lives under, inside the SPONSOR's own object.
 *
 * Named here rather than built inline at the call site because two places need to agree on it:
 * the route that holds the stake, and `expireEscrow`, which must NOT sweep one. A sponsor escrow
 * is parked 120 days out so it cannot expire under a live sponsorship, but the sweep collects
 * every due `held` row rather than only the one its alarm fired for, so past day 120 it would
 * return the principal with no premium and leave D1 still claiming an active stake. Resolution is
 * the only thing allowed to touch these rows, and this prefix is how the sweep knows.
 */
export const SPONSOR_ESCROW_PREFIX = 'sponsor:'
export const sponsorEscrowId = (sponsorshipId: string): string =>
  `${SPONSOR_ESCROW_PREFIX}${sponsorshipId}`
export const isSponsorEscrow = (escrowId: string): boolean =>
  escrowId.startsWith(SPONSOR_ESCROW_PREFIX)

/**
 * THE FLOOR UNDER THE LADDER. GAME-DESIGN 6.3's NPC Sponsor of Record.
 *
 * "A player who reaches Era day 10 without a human sponsor is offered a vouch by the NPC patron
 * of their home district... NPC vouching works, and it is strictly worse... It is a floor, not
 * a path."
 *
 * Ten days is measured from ERA OPEN and not from the account, which is 6.3 read literally. The
 * consequence, worth stating because nobody has ruled on it: a player who signs up on Era day 40
 * is eligible the moment they arrive, with no grace period in which to find a human sponsor.
 * That is the text, and the text is not obviously wrong, because by day 40 the city is full of
 * Capos and the house vouch is the one nobody wants. Logged in docs/CONTENT-GAPS.md.
 */
export const HOUSE_VOUCH_AFTER_DAYS = 10
export const HOUSE_VOUCH_AFTER_MS = HOUSE_VOUCH_AFTER_DAYS * DAY_MS

/**
 * THE ONLY LEGEND ANYTHING IN THIS GAME CAN AWARD, and it is GAME-DESIGN 6.3's own number.
 *
 * 6.3 publishes the whole table: a sponsee reaching Soldier is +40 to the sponsor, Capo is +120,
 * still active at Era end is +60, and an indictment for cheating inside 30 days is -200 with the
 * stake forfeited. Phase 13 builds the first row and names the other three at DESIGN-DELTAS 1t.15.
 *
 * **THE ROW WAS CHOSEN RATHER THAN INVENTED, and that is the point of it.** Legend is the headline
 * permanent-progression number, the twelve-week wipe exists to make it precious, 10.4 sells a
 * market listing slot for 3,000 of it, and nothing in this repository could award one point.
 * Picking a formula would have been a game-design decision taken in a commit. Taking the published
 * row is not, and it pays exactly the act the whole retention model is built on.
 *
 * It also has the property 10.4 says Legend must have. Era modifiers exist "to stop Legend from
 * being a synonym for playtime", and an award keyed to somebody ELSE's progression cannot be
 * farmed by grinding: a sponsor has at most two slots an Era.
 */
export const LEGEND_SPONSEE_SOLDIER = 40

/* -------------------------------------------------------------- refills */

/** Every consumed refill adds +15 Heat, so a player who refills to grind walks up the curve. */
export const REFILL_HEAT = 15
export const REFILL_CAP_ENERGY_PER_DAY = 2
export const REFILL_CAP_NERVE_PER_DAY = 1

/**
 * A ROLLING 24 hours, not a midnight reset.
 *
 * A fixed daily reset hands the whole population two refills at the same instant, which is a
 * server spike and a scheduling puzzle rather than a decision. It also rewards timezone
 * arbitrage: play at 23:55, refill, play at 00:05, refill again.
 */
export const REFILL_WINDOW_MS = 24 * 3600_000

/**
 * The Heat-drop refill: "dropping Heat below 20 from above 60 in a single day", GAME-DESIGN 2.3.
 *
 * It is the one refill you earn by playing the Heat mechanic rather than by logging in, and it
 * is deliberately the same shape as the mechanic it rewards: you have to go hot to qualify and
 * cold to claim, which is the whole loop in one grant.
 */
export const REFILL_HEAT_HIGH = 60
export const REFILL_HEAT_LOW = 20

/* ------------------------------------------------------------------- the Morning Line */

/**
 * THE CARD. `docs/PHASE-6B.md` section 6.
 *
 * An integer 0 to 7, Era-scoped, computed lazily from the day it was last claimed in exactly the
 * style every other bar in the game uses. No alarm, no cron, no nightly job.
 *
 *     daysMissed = ashDay(now) - cardDay - 1
 *     card       = max(0, card - daysMissed)
 *     on claim:  card = min(7, card + 1)
 *
 * **A single missed day costs net zero**: one decrement and one increment. Two missed days cost
 * one place. That is the specific inverse of the reset-to-day-1 streak the design literature
 * identifies as the predatory core of streak design, and the sentence that states it is the most
 * important string in the feature: *Miss one day and you keep your place. Miss two and you drop
 * one.*
 */
export const CARD_MAX = 7

/**
 * What the Card pays, and it is HEAT RELIEF and nothing else.
 *
 * It starts at the current flat `REFILL_HEAT`, so day one costs exactly what today costs and
 * nothing is nerfed to make room for the reward. Not more refills, because the two-per-day cap is
 * a monetisation pillar. Not cash, not XP, not a badge.
 *
 * Heat means the same thing to a level 4 Associate and a level 60 Boss, it is worth more the
 * hotter you are, so it is a decision rather than a button, **and it cannot be bought at any price
 * because Heat is not for sale.**
 */
export const CARD_HEAT = [15, 15, 14, 12, 10, 8, 6, 4] as const

/** The Heat a refill costs at a given Card position. */
export const cardHeatCost = (card: number): number =>
  CARD_HEAT[clamp(Math.floor(card), 0, CARD_MAX)]

/** Which day of the Era a timestamp falls in. Day 1 is the day the Era opened. */
export const ashDay = (now: number, eraStartsAt: number): number =>
  Math.max(1, Math.floor((now - eraStartsAt) / 86_400_000) + 1)

/**
 * The Card after `now`, given where it was and when it was last claimed.
 *
 * Pure, so the client derives the same number the server does from the same three inputs, exactly
 * as `settle()` does for the bars. Nothing here writes; the decay is applied on the next claim.
 */
export function cardNow(card: number, cardDay: number, day: number): number {
  if (cardDay === 0) return 0
  const missed = Math.max(0, day - cardDay - 1)
  return clamp(card - missed, 0, CARD_MAX)
}

/** True when today's claim has not been taken. Claiming is unconditional and instant. */
export const cardClaimable = (cardDay: number, day: number): boolean => cardDay < day

/**
 * THE CLAIM IS SEPARATE FROM THE SPEND, and three things fall out of that.
 *
 * Turning up is always enough, so the streak never punishes somebody who logged in and had
 * nothing worth spending on. The spend becomes a real decision instead of a reflex. And `daily`
 * and `family_gift` collapse into one code path, because both are now "a grant in your pocket".
 *
 * At most two unspent per kind, each expiring 72 hours after issue. That is Torn's inactivity
 * refill with the farm removed, and the cap is 1/50 of the post-nerf Torn number that produced
 * forum threads titled "I have 270 free refills".
 */
export const GRANT_TTL_MS = 72 * 3_600_000
export const GRANT_BANK_CAP = 2

/**
 * THE SPEND GATE, which closes a hole GAME-DESIGN:96 diagnoses and then accidentally recreates.
 *
 * "No overfill above E_max, ever" is right and is kept. What it leaves open is the drain-to-zero
 * optimum: if a refill sets the bar to full, the correct play is always to burn to nothing first,
 * which is precisely the Mafia Wars behaviour that section diagnoses.
 *
 * So the server refuses a refill while the resource is above a quarter of maximum, with the reason
 * printed on the bar, and below that line a refill always returns at least three quarters. Inside
 * the window every refill is worth between 75 and 100 percent of a bar, so there is no longer a
 * moment worth waiting for and the optimum disappears.
 */
export const REFILL_SPEND_CEILING = 0.25
export const REFILL_MIN_RETURN = 0.75

/** What a refill actually gives, from where the bar is now. Never overfills. */
export const refillTo = (current: number, max: number): number =>
  Math.max(current, Math.min(max, Math.round(max * REFILL_MIN_RETURN)), max)

/* ------------------------------------------------------ section 9.4 front businesses */

/**
 * A player may own up to FOUR fronts. FOUR, for everybody, forever.
 *
 * GAME-DESIGN 9.4 read "up to 4 (5 with Donator)" and that clause is deleted. A fifth front is
 * more clean income per day AND another perk generator, and the perks are not cosmetic: "Dispatch
 * (next 5 crimes cost 1 less Energy)" and "Long Way Round (evade one Mug)" are capability, and
 * they are tradeable to other players for clean cash. That is a capability purchase, and the
 * login page says "Nothing you buy makes you stronger".
 *
 * It is also the exact shape of the hole that was already found and closed once, in the Legend
 * pricing of cosmetics: a paid branch on a GAMEPLAY CONSTANT rather than on a reward entry, which
 * the content gate cannot see because it walks reward catalogues and this is a number in a source
 * file. `tools/lint/ashport-lint.mjs` now refuses the class outright: no exported constant in
 * `shared/formulas.ts` may be keyed on donator, premium, subscriber, supporter or paid status.
 *
 * Recorded in docs/DESIGN-DELTAS.md and docs/MONETIZATION.md.
 */
export const BUSINESS_OWN_CAP = 4

/**
 * Front income STOPS at 60 hours uncollected, exactly as territory does.
 *
 * Punishes five days away rather than two. An uncapped front is an idle game, and an idle game
 * is the thing the 90-second burst session exists instead of.
 */
export const BUSINESS_ACCRUAL_CAP_HOURS = 60

/* ------------------------------------------------------------ resource periods */

export const PERIODS = {
  energy: ENERGY_PERIOD_MS,
  nerve: NERVE_PERIOD_MS,
  heat: HEAT_PERIOD_MS,
} as const


/**
 * Where holding blocks stops paying.
 *
 * `upkeepDaily` is superlinear and block income is linear, so net income has a PEAK and then a
 * cliff. Against 8.3's average block the peak is SIX blocks and net turns negative at THIRTEEN.
 *
 * The Territory screen said "Past thirty blocks the map starts costing more than it pays". That
 * was wrong by a factor of 2.3 against the formula rendered forty lines above it, and it is the
 * kind of wrong that reads as authoritative.
 *
 * NO HARD CAP. An earlier draft of this ruling proposed capping a family at six blocks, on the
 * grounds that six is where the curve peaks. It is, but capping there removes DENIAL: holding
 * ground at a loss to squeeze a rival is legitimate play and one of the few things that makes a
 * map interesting. The curve already makes hoarding a losing move, which is the design worth
 * keeping. A cap would also not close the real hole, which is below.
 *
 * THE REAL HOLE IS SHARDING, and superlinear upkeep cannot close it. Forty one-block shell
 * families pay 40 x 8,000 = $320,000 a day where one family holding forty pays $2,433,789. That
 * is 7.6x cheaper, in closed form, for any coalition willing to run shells. The mitigation is not
 * economic and must not be: a family holds no territory until it has twelve sponsored members off
 * probation, which reuses the crest gate and the sponsor stake that already exist. Phase 7 owns
 * it; this comment exists so Phase 7 does not rediscover it the hard way.
 */
export const TERRITORY_PEAK_BLOCKS = 6
export const TERRITORY_CLIFF_BLOCKS = 13

/**
 * The family roster cap. FORTY FIVE.
 *
 * Five numbers were live across the documents and it looked like a judgement call. It is not.
 * GAME-DESIGN 8.1 states it outright and then shows its working: "Hard cap 45. Structure: 1 Boss,
 * 1 Underboss, up to 5 Capos each leading a crew of up to 8 (40), plus the three appointed
 * posts", which sums to exactly 45 and to nothing else. And DATA-MODEL's capacity plan sizes the
 * Era at "roughly 120 FamilyDO (at 40 members average)", which is arithmetically impossible under
 * a cap of 12, 20 or 30, because an average cannot exceed a cap.
 *
 * The anti-alt work is NOT done by scaling perks with headcount or with contribution. Contribution
 * metrics that sum Energy throughput are won by scripts: Energy regenerates 360 a day against a
 * bar of 138 at level 32, so capturing all of it needs 2.6 spaced logins that a script always
 * makes and a human usually does not, and job steps pay at zero Heat and cannot be busted, so the
 * alt never surfaces on the Scanner. Any contribution term has to be RISK BEARING: Heat cleared,
 * PvP wins, War Window commits. Phase 6 owns that.
 */
export const FAMILY_ROSTER_CAP = 45
export const FAMILY_ROSTER_MIN = 12

/**
 * FIVE crews of EIGHT, and the Capo sits INSIDE their own crew.
 *
 * GAME-DESIGN 8.1's structure sentence is "1 Boss, 1 Underboss, up to 5 Capos each leading a
 * crew of up to 8 (40), plus the three appointed posts", and only one reading of it sums to the
 * hard cap of 45:
 *
 *     Boss 1  +  Underboss 1  +  5 crews x 8 seats (Capo included) 40  +  3 posts 3  =  45
 *
 * Counting the five Capos OUTSIDE their crews gives 1 + 1 + 5 + 40 + 3 = 50, which is not a
 * number anywhere in the corpus. The parenthetical "(40)" settles it: five eights are forty, and
 * forty plus the five other seats is the cap. `shared/test/formulas.test.ts` asserts the sum.
 *
 * DATA-MODEL 3.2 modelled six crews at a cap of ten, and the Families mockup shipped that: six
 * times ten is SIXTY seats inside a family that can hold forty-five. Recorded in DESIGN-DELTAS
 * 1i.3.
 */
export const FAMILY_CREW_COUNT = 5
export const FAMILY_CREW_CAP = 8
/** Boss, Underboss, Consigliere, Enforcer, Driver. Five seats that sit outside every crew. */
export const FAMILY_CREWLESS_SEATS = 5

/**
 * Probation. New members are On Ice: no vault, no upkeep vote, no War Window, no post abilities,
 * and their crimes generate no Family-level credit. Torn's Recruit Status is 72 hours flat and it
 * works; the sponsored discount is what makes a vouch mechanically worth seeking out.
 */
export const PROBATION_MS = 72 * 3600_000
export const PROBATION_SPONSORED_MS = 24 * 3600_000

/** An application that nobody answers falls off the board after a week. DATA-MODEL 3.2. */
export const APPLICATION_TTL_MS = 7 * 24 * 3600_000

/**
 * The tithe: automatic, leader-set, 4 to 12 percent of every member's DIRTY income.
 *
 * Automatic rather than voluntary is the whole point. GAME-DESIGN 8.2 is explicit that "relying
 * on voluntary donations produces a dead vault", and Bootleggers' automatic cut with a protected
 * floor is the shape that works.
 */
export const TITHE_MIN_PCT = 4
export const TITHE_MAX_PCT = 12
export const TITHE_DEFAULT_PCT = 6

/** The member's cut, in cents, rounded DOWN so the tithe can never exceed the payout. */
export const titheOn = (dirtyIn: Cents, pct: number): Cents =>
  cents(Math.floor((Math.max(0, dirtyIn) * clamp(pct, TITHE_MIN_PCT, TITHE_MAX_PCT)) / 100))

/**
 * The raid-proof floor: 40 percent of the vault balance at raid time is untouchable, so a lost
 * War Window is a bruise rather than a death. GAME-DESIGN 8.2. Derived, never stored: a stored
 * floor drifts away from the balance it is a fraction of.
 */
export const VAULT_RAID_FLOOR = 0.4
export const vaultRaidFloor = (balance: Cents): Cents => cents(Math.floor(balance * VAULT_RAID_FLOOR))

/**
 * The vault ceiling.
 *
 * DATA-MODEL 3.2 declares `treasury_cap INTEGER NOT NULL DEFAULT 250000000`. Every other money
 * integer in that document is CENTS, which would make this **$2,500,000**, and that reading does
 * not survive contact with the population the same corpus provisions for.
 *
 * The arithmetic uses only authored numbers. GAME-DESIGN 9.7 puts the clean faucet at 381,003
 * per DAU per day. A family at the ruled cap of 45 members therefore clears about **$17,145,135
 * of clean a day between them**, so a $2.5M ceiling is filled by roughly **three and a half
 * hours** of ordinary collective play, and every deposit after that is silently refused. A
 * ceiling a full family reaches before lunch is not a ceiling, it is a bug that presents as a
 * vault that has stopped working.
 *
 * Read as dollars it is $250,000,000: about fifteen days of a full family's whole clean income,
 * which is headroom rather than a constraint. The vault cap is not a balance lever in any case;
 * the raid-proof floor is the mechanic and it is a fraction of the balance rather than of the
 * cap.
 *
 * The literal is kept and the unit is corrected. Recorded in DESIGN-DELTAS 1i.5, and pinned by
 * `shared/test/formulas.test.ts` against the faucet rather than against any derived cost.
 */
export const FAMILY_TREASURY_CAP: Cents = dollars(250_000_000)

/**
 * Books: the Family's org-chart infrastructure level, 1 to 16, bought with clean cash.
 *
 * Phase 6 stores it and publishes it; Phase 7 spends it, because it only does anything through
 * `territorySkim(blocks, books)`. It is here rather than in the territory block above because
 * the column lives on the family row and a bound the schema CHECKs must have one home.
 */
export const BOOKS_MIN = 1
export const BOOKS_MAX = 16

/* ---------------------------------------------------- section 8.2 upgrade branches */

/**
 * THE COST OF A FAMILY UPGRADE LEVEL, and where the number comes from.
 *
 * It is derived from an authored line rather than chosen. GAME-DESIGN 9.7's clean-drain table
 * carries a row reading **"Books levels and Family upgrades | 54,000 | 15%"**, per DAU per day.
 * That is an absolute allocation, not a fraction of the faucet, and it is the only number in the
 * corpus that budgets this sink. The arithmetic:
 *
 *   - GAME-DESIGN 8.2 requires that "a 12-person Family can complete two branches in 12 weeks",
 *     and 8.1 makes 12 the minimum viable roster, so 12 is the size the budget must clear.
 *   - 54,000 x 12 members x 84 days = **$54,432,000** of clean for Books AND upgrades over an Era.
 *   - The line names exactly two things and weights neither, so upgrades take half:
 *     **$27,216,000 for two full branches**, or **$13,608,000 per six-level branch**.
 *   - "Escalating per tier" is 9.5's stated shape. The exponent is 1.55, reused from
 *     `upkeepDaily` rather than invented, so the game has one superlinear curve family and not
 *     two: `cost(L) = 300,000 * L^1.55`.
 *   - Sum over L = 1..6 is 46.1829, so a branch totals **$13,855,064** and two total
 *     **$27,710,128**, which is 50.9 percent of the authored line. Books gets the other 49.1
 *     percent and 15 levels to spend it on, which Phase 7 prices.
 *
 * Sanity check against the constraint it has to satisfy: $27,710,128 across 12 members over 84
 * days is $27,490 of clean per member per day, against a modelled clean faucet of $381,003 per
 * DAU per day. That is **7.2 percent of a member's clean income**, which is a real commitment
 * with a lot of headroom, which is what "cheap enough to complete" asks for.
 *
 * TWO OF THESE INPUTS ARE DERIVED AND NOT AUTHORED, and both are escalated in
 * docs/CONTENT-GAPS.md rather than quietly adopted:
 *
 *   - **The 300,000 coefficient.** It was chosen so that six levels land near half the authored
 *     line. The exponent is borrowed from `upkeepDaily` so the game has one superlinear curve
 *     family rather than two, which is defensible, but nothing in the corpus states either.
 *   - **The 50/50 split between Books and upgrades.** 9.7's row names two things and weights
 *     neither. Half is a reading, not a reading OF anything.
 *
 * Neither is a guess in the sense the project forbids, because both are bounded by an authored
 * total and the result is checked against 8.2's own completability constraint by a test. Both are
 * still numbers somebody should confirm, and the register is where that request lives.
 *
 * What is NOT provisional: whether this budget moves if front-business income is re-ruled. It
 * does not. DESIGN-DELTAS section 2 item 1 asks whether front income should rise 8x to 10x, which
 * would add roughly 96,000 per DAU per day to the clean faucet, a 25 percent increase, and would
 * make these levels 20 percent cheaper in real terms. It would NOT move the sticker price,
 * because 54,000 is an absolute allocation and front income is not one of the lines in 9.7's
 * clean-faucet table at all.
 */
export const FAMILY_UPGRADE_MAX_LEVEL = 6
export const FAMILY_UPGRADE_BASE = 300_000
export const FAMILY_UPGRADE_EXPONENT = 1.55

/** Clean cash for ONE level. Level is the level being bought, so the first purchase is level 1. */
export const familyUpgradeCost = (level: number): Cents =>
  level < 1 || level > FAMILY_UPGRADE_MAX_LEVEL
    ? cents(0)
    : dollars(FAMILY_UPGRADE_BASE * Math.pow(level, FAMILY_UPGRADE_EXPONENT))

/** What a whole branch costs from nothing to the cap. Used by the budget test and the UI. */
export const familyBranchTotal = (): Cents =>
  cents(
    Array.from({ length: FAMILY_UPGRADE_MAX_LEVEL }, (_, i) => familyUpgradeCost(i + 1)).reduce(
      (a, b) => a + b,
      0,
    ),
  )

/**
 * The four branch effects, each reaching its GAME-DESIGN 8.2 cap at level 6 exactly.
 *
 * Linear in level, so the sixth level is worth the same as the first and the cap is the whole
 * ladder rather than a diminishing tail. Every one of these is a number 8.2 states; only the
 * per-level interpolation is ours, and it is the interpolation that makes the stated cap land
 * exactly at the stated last level.
 */

/** Laundering: "up to +5 points of laundering rate for all members". 500 bp at level 6. */
export const familyLaunderBonusBp = (level: number): number =>
  Math.round((500 * clamp(level, 0, FAMILY_UPGRADE_MAX_LEVEL)) / FAMILY_UPGRADE_MAX_LEVEL)

/** Discipline: "up to +25% Heat decay rate for all members". A multiplier on the decay rate. */
export const familyHeatDecayBonus = (level: number): number =>
  (0.25 * clamp(level, 0, FAMILY_UPGRADE_MAX_LEVEL)) / FAMILY_UPGRADE_MAX_LEVEL

/** Muscle: "up to +8% Power in War Windows". Feeds `PowerInput.familyBonus` as a fraction. */
export const familyWarPowerBonus = (level: number): number =>
  (0.08 * clamp(level, 0, FAMILY_UPGRADE_MAX_LEVEL)) / FAMILY_UPGRADE_MAX_LEVEL

/** Connections: "Precinct Captain 15% cheaper". The other two effects are step unlocks at 6. */
export const familyCaptainDiscount = (level: number): number =>
  (0.15 * clamp(level, 0, FAMILY_UPGRADE_MAX_LEVEL)) / FAMILY_UPGRADE_MAX_LEVEL

/**
 * Connections also grants "+1 sponsorship slot for every Capo" and a 48-hour Beat forecast.
 * Neither is a quantity that can be interpolated, so both land at the branch cap, which is the
 * only level the document names an effect for. Nothing in 8.2 assigns them to a middle level.
 */
export const FAMILY_CONNECTIONS_UNLOCK_LEVEL = FAMILY_UPGRADE_MAX_LEVEL

/* ------------------------------------------------ section 6.3 sponsorship capacity */

/**
 * Slots per Era, by the sponsor's own RANK rather than by their seat in a family.
 *
 * "Any player at Capo rank or above has 2 sponsorship slots per Era. Underboss and Boss have 3."
 * Associates and Soldiers have none, which is the scarcity the whole mechanic rests on.
 */
export const sponsorSlots = (rank: Rank, connectionsLevel = 0): number => {
  const base = rank === 'Boss' || rank === 'Underboss' ? 3 : rank === 'Capo' ? 2 : 0
  if (base === 0) return 0
  return base + (connectionsLevel >= FAMILY_CONNECTIONS_UNLOCK_LEVEL ? 1 : 0)
}

/**
 * The refill gift: one Nerve refill to a family member, on an eight-hour per-recipient cooldown,
 * costing the giver 40,000 dirty. GAME-DESIGN 2.3 and 9.5 sink #21.
 */
export const REFILL_GIFT_COST: Cents = dollars(40_000)
export const REFILL_GIFT_COOLDOWN_MS = 8 * 3600_000

/**
 * One text bound for every free-text field a Family owns: chat, announcements and the motto.
 *
 * 280 is not chosen here, it is `PITCH_MAX` from `server/src/lib/validate.ts`, which is the one
 * authored text length in the repository. Reusing it beats inventing a second, third and fourth
 * number for fields nobody has specified. If the writers want a longer announcement, that is a
 * number to author rather than to guess: logged in docs/CONTENT-GAPS.md.
 */
export const FAMILY_TEXT_MAX = 280
/** 2 to 4 characters, per DATA-MODEL 3.2's `tag TEXT NOT NULL -- 2 to 4 chars`. */
export const FAMILY_TAG_MIN = 2
export const FAMILY_TAG_MAX = 4

/**
 * Bounties are posted in DIRTY and paid in DIRTY.
 *
 * DATA-MODEL wanted a currency flip: post in clean, pay in dirty, deriving an anti-collusion
 * property from the laundering haircut a colluding pair would owe on every lap. The property is
 * real and it is BACKWARDS.
 *
 * One lap under the flip retains `0.9 * launderRate`. One lap under dirty-to-dirty with a 50
 * percent fee on top retains `1 / 1.5` = 0.667. They cross at a laundering rate of 0.741, and
 * `launderRate` reaches 0.78 at infrastructure tier 2 with the Laundering branch, which is one of
 * four branches that every family takes two of. So the flip is TIGHTER than dirty-to-dirty only
 * for players who have not built laundering, and LOOSER for exactly the organised families most
 * able to collude. It is regressive.
 *
 * It is also the weaker sink by 3.3x: a 10 percent burn on a voluntary transfer against 33 cents
 * destroyed per dollar on a claim and 47 on an expiry. GAME-DESIGN wants the Hitlist
 * laundering-adjacent, and a dirty-funded Hitlist keeps exposed money exposed, which is the
 * point.
 */
export const BOUNTY_FEE = 0.5
export const BOUNTY_EXPIRY_BURN = 0.2
export const BOUNTY_HOURS = 24

/* -------------------------------------------------------- section 9.2 the Fence */

/**
 * THE SALE FEE ON A PLAYER LISTING IS THE HEAT-SCALED FENCE FEE, and three documents said three
 * different things.
 *
 * GAME-DESIGN 9.2's listing table: "Sale fee: the Heat-scaled fence fee from 3.5,
 * `0.08 + 0.22 * (H/100)^1.6`, paid by the seller at the moment of sale."
 * DATA-MODEL 3.3's `fill.fee` column comment: "5% market fee, a clean-cash sink."
 * `game/src/screens/Market.tsx`: a header strip reading "Market Fees 2.5%".
 *
 * GAME-DESIGN wins, and not on seniority. 9.5's stated rule for every sink in the game is that
 * it must SCALE WITH POWER, because "a flat-cost sink is an inflation accelerator: it becomes
 * trivial to the players generating the most money". A flat 2.5 or 5 percent is exactly the
 * thing that rule forbids. The Heat-scaled curve is also already implemented, already tested,
 * already published as a 101-entry lookup table in the content bundle, and already the fee the
 * NPC fence charges, so using it here means one curve rather than three and one number on the
 * screen rather than a constant nobody can move.
 *
 * The 2.5 percent figure came from a mockup header. Recorded in docs/DESIGN-DELTAS.md.
 */
export const marketFeeBp = (sellerHeat: number, warrantPoints = 0): number =>
  fenceFeeBp(sellerHeat, warrantPoints)

/** A listing runs 7 days, then the item returns and the fee is not refunded. GAME-DESIGN 9.2. */
export const MARKET_LISTING_MS = 7 * 24 * 3600_000

/**
 * Per-buyer cap: 8 purchases per buyer per seller per rolling 24 hours, "so the Fence cannot be
 * a laundering pipe". GAME-DESIGN 9.2.
 */
export const MARKET_PAIR_CAP = 8
export const MARKET_PAIR_WINDOW_MS = 24 * 3600_000

/** How deep a single buy walks the book. One order must not be able to sweep a whole shard. */
export const MARKET_MATCH_LIMIT = 32

/**
 * The NPC black market. Dirty cash only, priced ABOVE book so it is a sink rather than an
 * arbitrage: GAME-DESIGN 2.4 lists "black-market gear from NPC vendors" as one of the five
 * things dirty cash spends on, and 9.2's price floor of 2x Crusher value is what stops a player
 * buying here and dumping on the Fence for a profit.
 *
 * At a 1.25 markup in dirty and a clean resale capped by the market, the round trip through the
 * fence fee and the laundering haircut is always a loss, which is the point.
 */
export const BLACK_MARKET_MARKUP = 1.25
export const blackMarketPrice = (base: Cents): Cents => scaleCents(base, BLACK_MARKET_MARKUP)

/** Laundering below this is dust and only exists to spam the ledger. */
export const LAUNDER_MIN: Cents = dollars(100)

/**
 * CONTRABAND DROPS. What feeds the Fence, and therefore what makes dirty cash exist as
 * something other than a payout number.
 *
 * A clean crime yields a contraband item with this probability, drawn from the authored
 * `content/items/contraband.json` catalogue at a tier the risk tier decides. There is no
 * per-crime authored drop table anywhere in either bible, so the rule is mechanical rather than
 * authored and the item is never invented in code: it is always a row from the catalogue.
 * Logged in docs/CONTENT-GAPS.md as content work.
 */
/*
 * THESE FOUR NUMBERS ARE DERIVED, NOT AUTHORED, and they are flagged for a ruling.
 *
 * GAME-DESIGN 9.1 says contraband IS the crime drop ("Crime drops, evidence sets, fenced not
 * listed") and CONTENT-BIBLE 8.6 calls it "the main thing the market screen actually trades", and
 * neither states a rate. The first pass used 6 to 24 percent, which the arithmetic says is too
 * low to be a mechanic:
 *
 *   A low-risk crime pays about 7,550 dirty at level 1. A tier 1 contraband drop fences for 4,200
 *   gross, about 3,860 net at Heat 0. At 6 percent that is 232 of expected value on a 7,550
 *   crime, or THREE PERCENT. At three percent it is a rare surprise, not the thing that feeds
 *   the Fence, and the Heat-scaled fence fee it is supposed to teach never gets exercised.
 *
 * At the rates below the same crime carries about 9 percent of its value in goods, and an extreme
 * crime carries about 24 percent, which puts the goods on the risky jobs where they belong.
 *
 * MONEY SUPPLY IMPACT, because it is a faucet: GAME-DESIGN 9.7 budgets crime payouts net of busts
 * at 420,000 of an 825,000 dirty faucet per DAU per day. Adding roughly 12 percent to crime output
 * is about 50,000, or 6 percent of the faucet. It is taxed twice on the way out, by the fence fee
 * (8 to 30 percent) and then by the laundering haircut (15 to 51 percent), so the net addition to
 * circulating money is smaller than the gross. Even so it is a real change to a balanced model and
 * the owner should rule on it rather than inherit it.
 */
export const CONTRABAND_DROP_CHANCE: Record<Risk, number> = {
  low: 0.15,
  medium: 0.2,
  high: 0.28,
  extreme: 0.36,
}

/** The highest item tier a crime of this risk can drop. */
export const CONTRABAND_DROP_TIER: Record<Risk, number> = { low: 1, medium: 2, high: 3, extreme: 4 }
